import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import { Send, MessageCircle, ChevronUp, ChevronDown, HelpCircle, X, XCircle, Mic, MicOff, Loader2, Eye, RotateCcw, Settings, MapPin, Film, Sparkles, Lightbulb, Info, ArrowRight } from 'lucide-react';
import { useContent } from './services/contentService';
import { ESCENARIOS as ESCENARIOS_CONFIG } from './config';
import apiService from './services/api';
import { useLocalSTT } from './hooks/useLocalSTT';
import { useLocalStorage } from './hooks/useLocalStorage';
import ChatMessage from './components/ChatMessage';
import SetupScreen from './components/SetupScreen';
import Thermometer from './components/Thermometer';
import AnimatedAvatar from './components/AnimatedAvatar';
import LemonSliceAvatar from './components/LemonSliceAvatar';
import { isLemonSliceEnabled } from './services/lemonsliceService';
import VideoAvatar from './components/VideoAvatar';
import { isVideoAvatarEnabled } from './services/videoAvatarService';
import LanguageSelector from './components/LanguageSelector';
import FeedbackView from './components/FeedbackView';
import BackgroundStack from './components/background/BackgroundStack';
import { I18nProvider, useI18n } from './i18n';
import { useTemperature } from './hooks/useTemperature';

/**
 * Detecta si un texto luce como el payload JSON de feedback ({sections:[...]}).
 * Sirve de fallback cuando el backend no manda `phase: feedback` en message_done.
 */
function looksLikeFeedbackJson(text) {
    if (!text) return false;
    let s = String(text).trim();
    const fenceMatch = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (fenceMatch) s = fenceMatch[1].trim();
    if (!s.startsWith('{')) return false;
    try {
        const parsed = JSON.parse(s);
        return !!(parsed && Array.isArray(parsed.sections) && parsed.sections.length > 0);
    } catch (_) {
        return false;
    }
}

/** Personaje del escenario: contrato backend `nombre_personaje` / `cargo_personaje`; se aceptan claves legadas por compatibilidad. */
function pickPersonajeEscenario(scenario) {
    if (!scenario) return { nombre_personaje: null, cargo_personaje: null };
    return {
        nombre_personaje: scenario.nombre_personaje ?? scenario.interlocutor_nombre ?? null,
        cargo_personaje: scenario.cargo_personaje ?? scenario.interlocutor_rol ?? null,
    };
}

function AppInner({ onLangChange }) {
    const { t } = useI18n();
    // Contenido dinámico desde ContentContext (con fallback automático)
    const { content, isLoadingContent } = useContent();

    // Estados principales
    const [messages, setMessages] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [scenarioInfo, setScenarioInfo] = useState(null);
    const [isFirstMessage, setIsFirstMessage] = useState(true);
    const [currentPhase, setCurrentPhase] = useState('recepcion');
    const [currentSkill, setCurrentSkill] = useState('');
    const [evaluatedSkills, setEvaluatedSkills] = useState([]);
    const [pendingSkills, setPendingSkills] = useState([]);
    const [turnosDesafio, setTurnosDesafio] = useState(1);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedLanguage, setSelectedLanguageLocal] = useState('es');
    const setSelectedLanguage = useCallback((lang) => {
        setSelectedLanguageLocal(lang);
        onLangChange?.(lang);
    }, [onLangChange]);

    const contentSlice = useMemo(() => {
        if (!content?.byLang) return content;
        const L = selectedLanguage === 'en' ? 'en' : 'es';
        return content.byLang[L] || content.byLang.es;
    }, [content, selectedLanguage]);
    const ESCENARIOS = contentSlice?.escenarios || {};

    const [selectedSkills, setSelectedSkills] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [isSelectingScenario, setIsSelectingScenario] = useState(false);
    // Hint overlay: aparece brevemente cerca del avatar; el bombillo lo recuerda
    const [hintOverlayVisible, setHintOverlayVisible] = useState(false);
    const [lastSeenHint, setLastSeenHint] = useState('');
    const [hintHistory, setHintHistory] = useState([]);
    const hintTimerRef = useRef(null);
    const sessionStartRef = useRef(Date.now());
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [newThreadId, setNewThreadId] = useState('');
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [legendOpen, setLegendOpen] = useState(false);
    const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
    const settingsMenuRef = useRef(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [lastMessageWasVoice, setLastMessageWasVoice] = useState(false);
    const [isVoiceTranscribing, setIsVoiceTranscribing] = useState(false);
    const [hud, setHud] = useState(null);
    const [challengeLabel, setChallengeLabel] = useState('');
    const [challengeIndex, setChallengeIndex] = useState(null);
    const [challengePoolDetail, setChallengePoolDetail] = useState(null);
    const [masterySignals, setMasterySignals] = useState(null);
    const [criteriaDetail, setCriteriaDetail] = useState(null);
    const [progress, setProgress] = useState(0);
    const { temperature, setTemperature, applySignal: applyTemperatureSignal, reset: resetTemperature } = useTemperature(50);
    // Estado del modo Arena (espejo del backend; ver spec /api/simulation)
    const [experienceMode, setExperienceMode] = useState('ambiental');
    const [negotiationPhase, setNegotiationPhase] = useState('');
    const [walkAwayProximity, setWalkAwayProximity] = useState(0);
    const [walkAwayPrev, setWalkAwayPrev] = useState(0);
    const [walkAwayDirection, setWalkAwayDirection] = useState('flat');
    const [hiddenObjectivesSurfaced, setHiddenObjectivesSurfaced] = useState({});
    const [arenaNudges, setArenaNudges] = useState({});
    const [pivotalMoments, setPivotalMoments] = useState([]);
    const lastHintRef = useRef('');
    const [stance, setStance] = useState('');
    const [bodyLanguage, setBodyLanguage] = useState('');
    const [isFeedbackTransitioning, setIsFeedbackTransitioning] = useState(false);
    const [hint, setHint] = useState('');
    const [turnosMinimos, setTurnosMinimos] = useState(5);
    const [turnosMaximos, setTurnosMaximos] = useState(7);

    // Contexto escénico (scene_moment, visit_stage, scene_moments_shown)
    const [sceneMoment, setSceneMoment] = useState('');
    const [sceneMomentId, setSceneMomentId] = useState('');
    const [visitStage, setVisitStage] = useState('');
    const [sceneMomentsShown, setSceneMomentsShown] = useState([]);
    const [activeSceneMoment, setActiveSceneMoment] = useState('');
    const sceneMomentTimerRef = useRef(null);
    // Set síncrono con los scene_moment_id ya volcados al chat (evita duplicados
    // cuando el mismo scene llega primero por SSE y luego en el payload final de onDone).
    const renderedSceneIdsRef = useRef(new Set());
    // Flag: si onScene ya actualizó visit_stage durante este stream, onDone no lo sobreescribe.
    const visitStageFromSceneRef = useRef(false);

    // Briefing card (mostrado antes del primer mensaje)
    const [briefingDismissed, setBriefingDismissed] = useState(false);

    // localStorage hooks
    const [threadId, setThreadId] = useLocalStorage('thread_id', null);
    const [storedScenario, setStoredScenario] = useLocalStorage('scenario', null);
    const [storedSkills, setStoredSkills] = useLocalStorage('selected_skills', []);
    const [storedLevel, setStoredLevel] = useLocalStorage('selected_level', null);
    // Modo manos libres = comportamiento habitual del mic. Default ON: tras
    // el saludo del personaje, el mic arranca solo y la voz del usuario se
    // auto-envía tras un silencio. El botón del mic apaga este modo (y el
    // mic) cuando el usuario quiere silencio explícito; volver a clickearlo
    // lo reactiva.
    // Clave _v2: la _v1 (hands_free_mode) tenía default false guardado en
    // localStorage de sesiones viejas, lo que bloqueaba el auto-arranque del
    // mic tras cambiar el default a true. _v2 ignora aquellos valores.
    const [handsFreeMode, setHandsFreeMode] = useLocalStorage('hands_free_mode_v2', true);

    // Preferencia del usuario entre los modos de avatar disponibles. Solo
    // entra en juego cuando AMBOS están habilitados vía env vars; si solo
    // uno está disponible, ese gana sin toggle.
    //  - 'video'      → <VideoAvatar> (mp4 en loop, sin lipsync)
    //  - 'lemonslice' → <LemonSliceAvatar> (lipsync remoto vía Daily.co)
    const [avatarModePref, setAvatarModePref] = useLocalStorage('avatar_mode_v1', 'video');

    // Modo realmente activo: cruza la preferencia con lo habilitado por env.
    // Si ninguno de los dos está habilitado, cae a <AnimatedAvatar>.
    const lemonAvailable = isLemonSliceEnabled();
    const videoAvailable = isVideoAvatarEnabled();
    const canToggleAvatar = lemonAvailable && videoAvailable;
    const activeAvatarMode = canToggleAvatar
        ? avatarModePref
        : lemonAvailable ? 'lemonslice'
        : videoAvailable ? 'video'
        : 'animated';
    const isLemonActive = activeAvatarMode === 'lemonslice';
    const isVideoActive = activeAvatarMode === 'video';

    // Referencias
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const ttsAudioRef = useRef(null);
    const avatarRef = useRef(null);
    // Ref sincronizado con currentPhase para uso dentro de closures de streaming
    const phaseRef = useRef(currentPhase);

    // Reproducir TTS usando API REST. Si Lemon Slice está activo, el blob se
    // redirige al avatar para que haga lipsync remoto vía Daily.co; en otros
    // modos se reproduce localmente.
    const playTTS = React.useCallback(async (text) => {
        try {
            if (!text || !text.trim()) return;
            if (ttsAudioRef.current) {
                try {
                    ttsAudioRef.current.pause();
                    ttsAudioRef.current.currentTime = 0;
                } catch (_) {}
                ttsAudioRef.current = null;
            }
            setIsTtsLoading(true);
            const audioBlob = await apiService.textToSpeech(text, selectedLanguage, scenarioInfo?.voice);
            setIsTtsLoading(false);
            setIsTtsPlaying(true);

            // Path remoto solo para Lemon Slice (audio sale por el avatar con
            // lipsync). VideoAvatar es "doblaje": el audio TTS suena local
            // mientras el video se reproduce en mute, así que saltamos esta
            // rama cuando el usuario eligió VideoAvatar.
            if (isLemonActive && avatarRef.current) {
                const durationSec = (await avatarRef.current.pushAudio(audioBlob)) || 0;
                // Tail más largo: el audio que oye el usuario viene del room
                // remoto de Lemon Slice con RTT + procesamiento (≈800-1800ms)
                // por encima del buffer local. Sin este margen el mic se
                // abre antes de que termine de sonar.
                const tailMs = Math.max(1200, Math.ceil(durationSec * 1000) + 1800);
                setTimeout(() => setIsTtsPlaying(false), tailMs);
                return;
            }

            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            ttsAudioRef.current = audio;
            audio.onended = () => {
                setIsTtsPlaying(false);
                ttsAudioRef.current = null;
            };
            await audio.play();
            addLog('🔊 Reproducido', 'success');
        } catch (error) {
            console.error('Error al reproducir audio:', error);
            setIsTtsLoading(false);
            setIsTtsPlaying(false);
        }
    }, [selectedLanguage, scenarioInfo?.voice]);

    // Estados de TTS local (via REST)
    const [isTtsPlaying, setIsTtsPlaying] = useState(false);
    const [isTtsLoading, setIsTtsLoading] = useState(false);

    // Subtítulos tipo cine: frase actual que el avatar está reproduciendo
    // (se actualiza por cada item de la cola al empezar a sonar; se vacía
    // al terminar). El toggle se persiste en localStorage.
    const [currentSpokenSentence, setCurrentSpokenSentence] = useState('');
    const [subtitlesEnabled, setSubtitlesEnabled] = useState(() => {
        try { return localStorage.getItem('subtitlesEnabled') !== '0'; } catch { return true; }
    });
    const toggleSubtitles = useCallback(() => {
        setSubtitlesEnabled(prev => {
            const next = !prev;
            try { localStorage.setItem('subtitlesEnabled', next ? '1' : '0'); } catch (_) {}
            return next;
        });
    }, []);

    // Avatar listo (video mp4 / Lemon Slice conectado). Bloquea el botón
    // Iniciar del briefing y el mic hasta que el componente avise vía onReady.
    // Default true: sin avatar pesado (AnimatedAvatar / sin videoUrl) no hay
    // nada que esperar.
    const [isAvatarReady, setIsAvatarReady] = useState(true);

    // Resetea isAvatarReady cuando entra/cambia el escenario o el usuario
    // alterna entre modos de avatar. Lemon Slice y video mp4 (con URL) son
    // los únicos que requieren carga; el resto pasa directo a true.
    useEffect(() => {
        if (!scenarioInfo) {
            setIsAvatarReady(true);
            return;
        }
        const needsLoad = isLemonActive || (isVideoActive && !!scenarioInfo.avatar_video_url);
        setIsAvatarReady(!needsLoad);
    }, [scenarioInfo?.avatar_video_url, scenarioInfo?.lemonslice_agent_id, isLemonActive, isVideoActive]);

    const handleAvatarReady = useCallback(() => setIsAvatarReady(true), []);
    const [logs, setLogs] = useState([]);
    const addLog = (message, type = 'info') => setLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
    const clearLogs = () => setLogs([]);

    // Transcripción local (vista previa) para feedback inmediato
    const { isSupported: isLocalSTTSupported, isActive: isLocalSTTActive, previewTranscript, start: startLocalSTT, stop: stopLocalSTT, reset: resetLocalSTT } = useLocalSTT(selectedLanguage);

    // No auto-iniciar STT: solo detener si hay TTS en reproducción
    useEffect(() => {
        if (isTtsPlaying && isLocalSTTActive) {
            stopLocalSTT();
        }
    }, [isTtsPlaying, isLocalSTTActive, stopLocalSTT]);

    // Hands-free: arranca el mic siempre que el sistema esté en estado de
    // "listo para escuchar". Se activa al cargar la página, al terminar TTS,
    // y tras enviar un mensaje. La única forma de mantener el mic apagado
    // de forma persistente es desactivar el toggle de hands-free.
    useEffect(() => {
        if (!handsFreeMode) return;
        if (!isInitialized) return;
        if (currentPhase === 'feedback') return;
        // Solo arrancar tras dismiss del briefing (no queremos grabar mientras
        // el usuario lee el modal inicial).
        if (!briefingDismissed) return;
        // Espera a que el avatar (video / Lemon Slice) esté listo antes de
        // habilitar el mic. Sin esto el usuario podría hablar mientras el
        // avatar aún no aparece, y la respuesta llegaría sobre una pantalla
        // en carga.
        if (!isAvatarReady) return;
        if (isLoading || isTtsLoading || isTtsPlaying) return;
        if (isLocalSTTActive) return;
        // 300ms de gracia para evitar arranques en transiciones rápidas
        const t = setTimeout(() => {
            if (!isLocalSTTActive) startLocalSTT();
        }, 300);
        return () => clearTimeout(t);
    }, [
        handsFreeMode,
        isInitialized,
        currentPhase,
        briefingDismissed,
        isAvatarReady,
        isLoading,
        isTtsLoading,
        isTtsPlaying,
        isLocalSTTActive,
        startLocalSTT,
    ]);

    // Hands-free: detectar silencio y auto-enviar.
    // Cada vez que cambia previewTranscript, reseteamos un timer; si llega a
    // cero sin nuevos cambios → silencio → enviar.
    const silenceTimerRef = useRef(null);
    const sendMessageRef = useRef(null);

    // Reflejar la vista previa en el input mientras se está transcribiendo
    useEffect(() => {
        if (!isLocalSTTActive) return; // si no está activo, no tocar el input
        // Si acabamos de enviar (input vacío), no reinyectar texto hasta que haya nuevo audio
        if (inputMessage === '' && !previewTranscript) return;
        if (previewTranscript && !isVoiceTranscribing) {
            setInputMessage(previewTranscript);
        }
    }, [isLocalSTTActive, previewTranscript, isVoiceTranscribing, inputMessage]);

    // Auto-resize del textarea:
    // - Crece libremente hasta MAX_INPUT_HEIGHT.
    // - Al superar ese límite activa scroll interno y auto-scrollea al final
    //   para que siempre se vea lo más reciente (especialmente en voz).
    const MAX_INPUT_HEIGHT = 200; // px ≈ 7-8 líneas
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const natural = el.scrollHeight;
        if (natural <= MAX_INPUT_HEIGHT) {
            el.style.height = `${natural}px`;
            el.style.overflowY = 'hidden';
        } else {
            el.style.height = `${MAX_INPUT_HEIGHT}px`;
            el.style.overflowY = 'auto';
            // Scroll al final para ver el texto más reciente
            el.scrollTop = el.scrollHeight;
        }
    }, [inputMessage]);

    // Limpiar vista previa cuando llega transcripción del servidor
    useEffect(() => {
        if (isVoiceTranscribing && isLocalSTTActive) {
            stopLocalSTT();
        }
    }, [isVoiceTranscribing, isLocalSTTActive, stopLocalSTT]);

    // Toggle único del mic: si está grabando, apaga hands-free + mic. Si
    // está apagado, reactiva hands-free (el effect arrancará el mic
    // automáticamente cuando las condiciones se cumplan). Limpia también
    // el timer de silencio en cualquier dirección.
    const toggleMic = useCallback(() => {
        if (isLocalSTTActive) {
            stopLocalSTT();
            resetLocalSTT();
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            setHandsFreeMode(false);
        } else {
            setHandsFreeMode(true);
        }
    }, [isLocalSTTActive, stopLocalSTT, resetLocalSTT, setHandsFreeMode]);

    // Atajo de teclado: espacio activa/desactiva el micrófono (toggle único)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code !== 'Space') return;
            const tag = document.activeElement?.tagName;
            if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'BUTTON') return;
            if (currentPhase === 'feedback') return;
            if (!isInitialized) return;
            e.preventDefault();
            toggleMic();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleMic, isInitialized, currentPhase]);

    // Función para manejar mensajes de voz
    function handleVoiceMessage(voiceData) {
        if (voiceData) {
            setBriefingDismissed(true);
            // Agregar mensaje del usuario
            setMessages(prev => [...prev, {
                role: 'user',
                content: voiceData.user
            }]);

            // Agregar respuesta del asistente
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: voiceData.assistant
            }]);

            // Actualizar estado de la simulación
            if (voiceData.fullResponse) {
                updateSimulationState(voiceData.fullResponse);
            }
        }
    }

    // Función para actualizar el estado de la simulación
    const updateSimulationState = (response) => {
        if (response.status === 'success') {
            console.log('📊 [updateSimulationState] Payload completo:', JSON.stringify(response, null, 2));
            console.log('📊 [updateSimulationState] Campos de progresión:', {
                phase: response.phase,
                progress: response.progress,
                challenge_index: response.challenge_index,
                challenge_label: response.challenge_label,
                challenge_pool_detail: response.challenge_pool_detail,
                criteria_detail: response.criteria_detail,
                turnos_desafio: response.turnos_desafio,
                turnos_minimos: response.turnos_minimos,
                turnos_maximos: response.turnos_maximos,
                habilidad_actual: response.habilidad_actual,
                habilidades_evaluadas: response.habilidades_evaluadas,
                habilidades_pendientes: response.habilidades_pendientes,
                mastery_signals: response.mastery_signals,
            });

            if (response.phase)              setCurrentPhase(response.phase);
            if (response.habilidad_actual)   setCurrentSkill(response.habilidad_actual);

            if (Array.isArray(response.habilidades_evaluadas) && response.habilidades_evaluadas.length > 0)
                setEvaluatedSkills(response.habilidades_evaluadas);
            if (Array.isArray(response.habilidades_pendientes) && response.habilidades_pendientes.length > 0)
                setPendingSkills(response.habilidades_pendientes);

            if (response.turnos_desafio  != null) setTurnosDesafio(response.turnos_desafio);
            if (response.turnos_minimos  != null) setTurnosMinimos(response.turnos_minimos);
            if (response.turnos_maximos  != null) setTurnosMaximos(response.turnos_maximos);
            if (response.progress        != null) setProgress(response.progress);
            if (response.stance)                  setStance(response.stance);
            if (response.body_language)           setBodyLanguage(response.body_language);
            if (response.hint)                   setHint(response.hint);

            console.log('🎭 stance:', response.stance, '| body_language:', response.body_language, '| hint:', response.hint);

            if (response.challenge_label     != null) setChallengeLabel(response.challenge_label);
            if (response.challenge_index     != null) setChallengeIndex(response.challenge_index);
            if (response.challenge_pool_detail != null) setChallengePoolDetail(response.challenge_pool_detail);
            if (response.mastery_signals     != null) setMasterySignals(response.mastery_signals);
            if (response.criteria_detail     != null) setCriteriaDetail(response.criteria_detail);

            // Modo Arena vs ambiental
            const mode = response.experience_mode || 'ambiental';
            setExperienceMode(mode);
            if (response.negotiation_phase) setNegotiationPhase(response.negotiation_phase);
            if (response.hidden_objectives_surfaced) setHiddenObjectivesSurfaced(response.hidden_objectives_surfaced);
            if (response.arena_nudges) setArenaNudges(response.arena_nudges);
            if (Array.isArray(response.pivotal_moments)) setPivotalMoments(response.pivotal_moments);

            const hintIsNew = response.hint && response.hint !== lastHintRef.current;
            if (response.hint) lastHintRef.current = response.hint;

            if (mode === 'arena') {
                // En Arena el termómetro lo dicta el backend (walk_away_proximity 0..1).
                const cur = response.negotiation_temperature ?? response.walk_away_proximity;
                const prev = response.walk_away_proximity_prev;
                const dir = response.walk_away_direction || 'flat';
                if (typeof cur === 'number') setTemperature(Math.max(0, Math.min(100, Math.round(cur * 100))));
                if (typeof prev === 'number') setWalkAwayPrev(prev);
                if (typeof cur === 'number') setWalkAwayProximity(cur);
                setWalkAwayDirection(dir);
            } else {
                // Modo ambiental: heurística local sigue rigiendo.
                applyTemperatureSignal({
                    masterySignals: response.mastery_signals,
                    stance: response.stance,
                    hint: response.hint,
                    hintIsNew,
                    progress: response.progress,
                });
            }

            if (response.visit_stage) {
                if (visitStageFromSceneRef.current) {
                    console.log('🎬 [updateSimulationState] visit_stage ignorado (ya actualizado por onScene):', response.visit_stage);
                } else {
                    console.log('🎬 [updateSimulationState] Actualizando visit_stage:', response.visit_stage);
                    setVisitStage(response.visit_stage);
                }
            }
            if (Array.isArray(response.scene_moments_shown)) setSceneMomentsShown(response.scene_moments_shown);

            processSceneMoment({
                scene_moment: response.scene_moment,
                scene_moment_id: response.scene_moment_id,
            });

            if (response.feedbackMessage) setFeedbackMessage(response.feedbackMessage);
        }
    };

    // Helper centralizado: añade el divider de scene_moment al chat si el id aún
    // no fue pintado (dedupe vía ref síncrono). Usado desde onScene (SSE) y
    // desde updateSimulationState (payload final de onDone) sin duplicar.
    const processSceneMoment = useCallback((data) => {
        if (!data) return;
        const incomingSceneId = data.scene_moment_id || '';
        const incomingSceneText = (data.scene_moment || '').trim();
        if (!incomingSceneText || !incomingSceneId) return;
        if (renderedSceneIdsRef.current.has(incomingSceneId)) return;

        renderedSceneIdsRef.current.add(incomingSceneId);
        setSceneMoment(incomingSceneText);
        setSceneMomentId(incomingSceneId);
        setActiveSceneMoment(incomingSceneText);

        setMessages(prev => [...prev, {
            role: 'scene',
            content: incomingSceneText,
            sceneId: incomingSceneId,
        }]);

        if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current);
        sceneMomentTimerRef.current = setTimeout(() => setActiveSceneMoment(''), 7000);
    }, []);

    // Limpiar timer del banner escénico al desmontar
    useEffect(() => {
        return () => {
            if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current);
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
        };
    }, []);

    // Humanizar etiquetas de etapas y momentos escénicos
    const getVisitStageLabel = (stage) => {
        const labels = selectedLanguage === 'en' ? {
            // Stages del Excel EN
            presentation: 'Presentation',
            initial_conversation: 'Initial Conversation',
            walkthrough: 'Site Walkthrough',
            closing: 'Closing',
            // Slugs genéricos por si el backend usa los mismos que ES
            entrada: 'Entrance',
            recepcion: 'Reception',
            saludo: 'Greeting',
            conversacion_inicial: 'Initial Conversation',
            recorrido: 'Site Walkthrough',
            instalaciones: 'Facilities',
            inspeccion: 'Inspection',
            oficina: 'Office',
            cierre: 'Closing',
            despedida: 'Farewell',
            seguimiento: 'Follow-up',
        } : {
            // Stages del Excel ES (incluye con mayúscula/acento tal como vienen)
            'Presentación': 'Presentación',
            presentation: 'Presentación',
            entrada: 'Entrada',
            recepcion: 'Recepción',
            saludo: 'Saludo',
            conversacion_inicial: 'Conversación inicial',
            initial_conversation: 'Conversación inicial',
            recorrido: 'Recorrido',
            walkthrough: 'Recorrido',
            instalaciones: 'Instalaciones',
            inspeccion: 'Inspección',
            oficina: 'Oficina',
            cierre: 'Cierre',
            closing: 'Cierre',
            despedida: 'Despedida',
            seguimiento: 'Seguimiento',
        };
        if (!stage) return '';
        return labels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Función para cargar mensajes previos
    const loadPreviousMessages = async (targetThreadId) => {
        try {
            const data = await apiService.loadPreviousMessages(targetThreadId);
            
            console.log('📡 Respuesta del backend:', data);
            
            // Verificar si hay error en la respuesta del backend
            if (data.checkpoints && data.checkpoints.status === 'error') {
                console.error('❌ Error del backend:', data.checkpoints.message);
                return false;
            }
            
            // Parser tolerante: el backend puede devolver el checkpoint en varias
            // formas. Probamos primero las rutas conocidas y, si ninguna coincide,
            // buscamos recursivamente el primer objeto que tenga "messages" (array)
            // y "phase" (string), que son los dos campos mínimos que usamos.
            const looksLikeCheckpoint = (obj) =>
                obj &&
                typeof obj === 'object' &&
                Array.isArray(obj.messages) &&
                (typeof obj.phase === 'string' || typeof obj.phase === 'undefined');

            const findCheckpointDeep = (node, depth = 0) => {
                if (!node || typeof node !== 'object' || depth > 4) return null;
                if (looksLikeCheckpoint(node)) return node;
                if (Array.isArray(node)) {
                    for (const item of node) {
                        const found = findCheckpointDeep(item, depth + 1);
                        if (found) return found;
                    }
                    return null;
                }
                for (const key of Object.keys(node)) {
                    const found = findCheckpointDeep(node[key], depth + 1);
                    if (found) return found;
                }
                return null;
            };

            let lastCheckpoint = null;
            const candidatePaths = [
                data?.last_checkpoint,
                data?.checkpoints?.last_checkpoint,
                data?.checkpoints?.checkpoint,
                data?.checkpoints?.state,
                data?.state,
                Array.isArray(data?.checkpoints)
                    ? data.checkpoints[data.checkpoints.length - 1]
                    : null,
                Array.isArray(data?.checkpoints?.checkpoints)
                    ? data.checkpoints.checkpoints[data.checkpoints.checkpoints.length - 1]
                    : null,
            ];
            for (const candidate of candidatePaths) {
                if (looksLikeCheckpoint(candidate)) {
                    lastCheckpoint = candidate;
                    break;
                }
            }
            if (!lastCheckpoint) {
                lastCheckpoint = findCheckpointDeep(data);
            }

            if (lastCheckpoint && Array.isArray(lastCheckpoint.messages)) {
                console.log('✅ Checkpoint encontrado, restaurando estado completo...');
                
                // Transformar mensajes
                const transformedMessages = lastCheckpoint.messages.map(m => ({
                    role: m.role || (m.type === 'human' ? 'user' : 'assistant'),
                    content: m.content || ''
                }));

                console.log(`📝 Restaurando ${transformedMessages.length} mensajes`);
                setMessages(transformedMessages);
                
                console.log(`🔄 Restaurando fase: ${lastCheckpoint.phase || 'recepcion'}`);
                setCurrentPhase(lastCheckpoint.phase || 'recepcion');
                setCurrentSkill(lastCheckpoint.habilidad_actual || '');
                setEvaluatedSkills(Array.isArray(lastCheckpoint.habilidades_evaluadas) ? lastCheckpoint.habilidades_evaluadas : []);
                setPendingSkills(Array.isArray(lastCheckpoint.habilidades_pendientes) ? lastCheckpoint.habilidades_pendientes : []);
                setTurnosDesafio(lastCheckpoint.turnos_desafio || 1);
                if (lastCheckpoint.feedbackMessage) {
                    setFeedbackMessage(lastCheckpoint.feedbackMessage);
                } else if (lastCheckpoint.phase === 'feedback' && transformedMessages.length > 0) {
                    const lastAssistant = [...transformedMessages].reverse().find(m => m.role === 'assistant');
                    setFeedbackMessage(lastAssistant?.content || 'feedback_completed');
                } else {
                    setFeedbackMessage('');
                }
                setChallengeLabel(lastCheckpoint.challenge_label || '');
                setChallengeIndex(lastCheckpoint.challenge_index ?? null);
                setChallengePoolDetail(lastCheckpoint.challenge_pool_detail ?? null);
                setMasterySignals(lastCheckpoint.mastery_signals ?? null);
                setCriteriaDetail(lastCheckpoint.criteria_detail ?? null);
                setProgress(lastCheckpoint.progress ?? 0);
                setStance(lastCheckpoint.stance || '');
                setBodyLanguage(lastCheckpoint.body_language || '');
                setHint(lastCheckpoint.hint || '');
                setTurnosMinimos(lastCheckpoint.turnos_minimos ?? 2);
                setTurnosMaximos(lastCheckpoint.turnos_maximos ?? 3);
                setVisitStage(lastCheckpoint.visit_stage || '');
                setSceneMomentsShown(Array.isArray(lastCheckpoint.scene_moments_shown) ? lastCheckpoint.scene_moments_shown : []);
                setSceneMoment(lastCheckpoint.scene_moment || '');
                setSceneMomentId(lastCheckpoint.scene_moment_id || '');
                setActiveSceneMoment('');

                // Inicializar el set de scene_ids ya renderizados con el último
                // visto para que un re-envío del mismo id no vuelva a pintarlo.
                renderedSceneIdsRef.current = new Set();
                if (lastCheckpoint.scene_moment_id) {
                    renderedSceneIdsRef.current.add(lastCheckpoint.scene_moment_id);
                }
                if (Array.isArray(lastCheckpoint.scene_moments_shown)) {
                    for (const id of lastCheckpoint.scene_moments_shown) {
                        if (id) renderedSceneIdsRef.current.add(id);
                    }
                }

                // Restaurar habilidades seleccionadas si están disponibles
                if (lastCheckpoint.habilidades_seleccionadas) {
                    setSelectedSkills(lastCheckpoint.habilidades_seleccionadas);
                }
                
                setIsFirstMessage(false);
                setIsInitialized(true);
                setBriefingDismissed(true);

                // Cargar información del escenario
                if (lastCheckpoint.escenario) {
                    console.log(`🎭 Restaurando escenario: ${lastCheckpoint.escenario}`);
                    const scenarioId = Object.keys(ESCENARIOS).find(id => 
                        ESCENARIOS[id].nombre === lastCheckpoint.escenario
                    );
                    if (scenarioId) {
                        setSelectedScenario(scenarioId);
                        const escRest = ESCENARIOS[scenarioId];
                        const pj = pickPersonajeEscenario(escRest);
                        setScenarioInfo({
                            nombre: lastCheckpoint.escenario,
                            tipo_proyecto: lastCheckpoint.tipo_proyecto || escRest.tipo_proyecto,
                            descripcion_breve: lastCheckpoint.descripcion_breve || escRest.descripcion_breve,
                            descripcion_detallada: lastCheckpoint.descripcion_detallada || escRest.descripcion_detallada,
                            instrucciones_actuacion: lastCheckpoint.instrucciones_actuacion || escRest.instrucciones_actuacion,
                            nombre_personaje:
                                lastCheckpoint.nombre_personaje
                                ?? lastCheckpoint.interlocutor_nombre
                                ?? pj.nombre_personaje,
                            cargo_personaje:
                                lastCheckpoint.cargo_personaje
                                ?? lastCheckpoint.interlocutor_rol
                                ?? pj.cargo_personaje,
                            // Campos Arena: vienen del escenario actual (no del
                            // checkpoint, que puede ser legacy sin estos campos).
                            hidden_objectives: Array.isArray(escRest.hidden_objectives) ? escRest.hidden_objectives : [],
                            walk_away_definition: escRest.walk_away_definition || '',
                            relacion_previa: escRest.relacion_previa || '',
                            lemonslice_agent_id: escRest.lemonslice_agent_id || '',
                            voice: escRest.voice || '',
                            avatar_video_url: escRest.avatar_video_url || '',
                        });
                        console.log(`✅ Escenario restaurado: ${scenarioId}`);
                    }
                } else {
                    console.log('⚠️ No hay información de escenario en el checkpoint');
                }

                return true;
            } else {
                console.warn('⚠️ No se encontró checkpoint válido en la respuesta');
                console.warn('📊 Estructura recibida:', {
                    topLevelKeys: Object.keys(data || {}),
                    checkpointsType: Array.isArray(data?.checkpoints) ? 'array' : typeof data?.checkpoints,
                    checkpointsKeys:
                        data?.checkpoints && typeof data.checkpoints === 'object' && !Array.isArray(data.checkpoints)
                            ? Object.keys(data.checkpoints)
                            : 'N/A',
                    lastCheckpointShape: lastCheckpoint
                        ? {
                              keys: Object.keys(lastCheckpoint),
                              hasMessages: Array.isArray(lastCheckpoint.messages),
                              messagesLen: Array.isArray(lastCheckpoint.messages) ? lastCheckpoint.messages.length : null,
                          }
                        : null,
                });
                return false;
            }
        } catch (error) {
            console.error('Error al cargar mensajes:', error);
            return false;
        }
    };

    // Función para enviar mensaje de texto
    // Función para verificar si el feedback ya fue enviado
    const isFeedbackCompleted = () => {
        return currentPhase === 'feedback' && feedbackMessage && feedbackMessage.trim() !== '';
    };

    // Función para verificar si la simulación está en fase de feedback
    const isInFeedbackPhase = () => {
        return currentPhase === 'feedback';
    };

    // Función para verificar si se debe mostrar el input
    const shouldShowInput = () => {
        return isInitialized && !isInFeedbackPhase() && !isFeedbackCompleted();
    };

    const sendMessage = async (messageText, { hidden = false } = {}) => {
        if (!messageText.trim() || isLoading || !shouldShowInput()) return;

        setIsLoading(true);
        setBriefingDismissed(true);
        const isFirstMsg = messages.length === 0;
        const wasVoiceMessage = lastMessageWasVoice;

        // Garantizar thread_id antes de salir al backend. Si llega null
        // (p.ej. tras Reiniciar + restauración por storedScenario sin
        // pasar por handleStart), el backend acaba en LangGraph con
        // thread_id=None y carga state aleatorio (incluye sesiones viejas
        // en feedback). Generamos uno aquí mismo como red de seguridad.
        let activeThreadId = threadId;
        if (!activeThreadId) {
            activeThreadId = `thread-${uuidv4().slice(0, 8)}`;
            setThreadId(activeThreadId);
        }

        console.log('📤 sendMessage - Estado de detección de voz:');
        console.log('  - lastMessageWasVoice:', lastMessageWasVoice);
        console.log('  - wasVoiceMessage:', wasVoiceMessage);
        console.log('  - messageText:', messageText);
        console.log('  - hidden:', hidden);

        setLastMessageWasVoice(false);

        // Cortar audio previo si lo había (evita solapamientos con el turno anterior)
        if (ttsAudioRef.current) {
            try {
                ttsAudioRef.current.pause();
                ttsAudioRef.current.currentTime = 0;
            } catch (_) {}
            ttsAudioRef.current = null;
        }

        try {
            // Agregar mensaje del usuario inmediatamente, salvo en modo hidden
            // (usado para el saludo invisible del botón "Iniciar" — se manda al
            // backend para arrancar la conversación pero no se muestra en chat).
            if (!hidden) {
                const userMessage = {
                    role: 'user',
                    content: messageText,
                    _voiceMessage: wasVoiceMessage
                };
                setMessages(prev => [...prev, userMessage]);
            }
            setInputMessage('');
            stopLocalSTT();
            resetLocalSTT();
            setTimeout(() => setInputMessage(''), 0);
            setIsVoiceTranscribing(false);

            console.log('🔄 Enviando mensaje - /simulation/stream');
            visitStageFromSceneRef.current = false;
            const sessionDurationSeconds = parseFloat(((Date.now() - sessionStartRef.current) / 1000).toFixed(1));

            // ─── Pipeline "texto + audio por frases" ─────────────────────────
            // Mantenemos un buffer acumulativo con los tokens recibidos y, cada
            // vez que detectamos el final de una frase, encolamos un TTS para
            // esa frase. El reproductor consume la cola en orden.
            const sentenceEnd = /[.!?…](?:["'»)\]]*)(?:\s|$)/;
            const stState = { buffer: '', lastCut: 0 };
            const audioQueue = [];
            const playingState = { value: false };
            let assistantIdx = null;
            let firstTokenReceived = false;
            let accumulatedText = '';

            const extractCompleteSentences = (chunk) => {
                stState.buffer += chunk;
                const out = [];
                let match;
                while ((match = stState.buffer.slice(stState.lastCut).match(sentenceEnd))) {
                    const end = stState.lastCut + match.index + match[0].length;
                    const sentence = stState.buffer.slice(stState.lastCut, end).trim();
                    if (sentence) out.push(sentence);
                    stState.lastCut = end;
                }
                return out;
            };

            const fetchSentenceAudio = async (text) => {
                if (!text || !text.trim()) return null;
                try {
                    const blob = await apiService.textToSpeech(text, selectedLanguage, scenarioInfo?.voice);
                    return new Audio(URL.createObjectURL(blob));
                } catch (err) {
                    console.error('Error TTS frase:', err);
                    return null;
                }
            };

            const pumpQueue = async () => {
                if (playingState.value) return;
                playingState.value = true;
                setIsTtsLoading(false);
                setIsTtsPlaying(true);
                while (audioQueue.length > 0) {
                    // Limpiar cola y parar si se llega a la fase de feedback
                    if (phaseRef.current === 'feedback') {
                        audioQueue.length = 0;
                        break;
                    }
                    const item = audioQueue.shift();
                    if (!item) continue;
                    const audio = await item.audio;
                    if (!audio) continue;
                    if (phaseRef.current === 'feedback') continue; // audio ya cargado pero fase cambió
                    ttsAudioRef.current = audio;
                    // Subtítulo tipo cine: una frase a la vez, sincronizada
                    // con el audio que empieza a sonar ahora.
                    if (item.text) setCurrentSpokenSentence(item.text);
                    if (isLemonActive && avatarRef.current) {
                        try { await avatarRef.current.attachAudioElement(audio); } catch (_) {}
                    }
                    await new Promise((resolve) => {
                        audio.onended = resolve;
                        audio.onerror = resolve;
                        audio.play().catch((e) => {
                            console.error('Error play:', e);
                            resolve();
                        });
                    });
                }
                playingState.value = false;
                // En modo Lemon Slice, el audio.onended del <audio> local
                // dispara cuando termina el buffer local, pero el usuario
                // sigue oyendo el remoto del room ≈1.5s después. Esperamos
                // antes de bajar el flag para que el mic no se abra encima.
                if (isLemonActive) {
                    await new Promise((r) => setTimeout(r, 1500));
                }
                setIsTtsPlaying(false);
                setCurrentSpokenSentence('');
                // Limpiamos también isTtsLoading: enqueueSpeech lo enciende
                // por cada frase nueva del stream y solo lo apagamos al
                // arrancar pumpQueue. Si entran frases mientras ya está
                // reproduciendo, el flag se queda colgado en true al drenar
                // la cola → "Preparando audio..." atascado.
                setIsTtsLoading(false);
                ttsAudioRef.current = null;
                addLog('🔊 Reproducido', 'success');
            };

            const enqueueSpeech = (text) => {
                if (!text || !text.trim()) return;
                // Solo marcamos loading cuando aún no hay nada reproduciéndose.
                // Si pumpQueue ya está corriendo, el usuario ya está oyendo
                // audio, no estamos "preparando" nada.
                if (!playingState.value) setIsTtsLoading(true);
                audioQueue.push({ audio: fetchSentenceAudio(text), text: text.trim() });
                pumpQueue();
            };

            await apiService.sendMessageStream(
                activeThreadId,
                messageText,
                isFirstMsg,
                scenarioInfo,
                selectedLanguage,
                selectedSkills,
                selectedLevel,
                {
                    onToken: ({ text }) => {
                        if (!text) return;
                        accumulatedText += text;

                        // No mostrar tokens ni reproducir audio en fase feedback
                        if (phaseRef.current === 'feedback') return;

                        // El payload de feedback llega como JSON o con code fence.
                        // El evento `phase: feedback` solo aparece en message_done,
                        // así que detectamos por la forma del texto para no
                        // pintar la burbuja ni arrancar TTS que después tendríamos
                        // que cortar abruptamente al cambiar de vista.
                        const head = accumulatedText.trimStart();
                        if (head.startsWith('{') || head.startsWith('```')) return;

                        if (!firstTokenReceived) {
                            firstTokenReceived = true;
                            setIsLoading(false);
                            setMessages(prev => {
                                assistantIdx = prev.length;
                                return [...prev, {
                                    role: 'assistant',
                                    content: text,
                                    _streaming: true
                                }];
                            });
                        } else {
                            setMessages(prev => {
                                if (assistantIdx == null || !prev[assistantIdx]) return prev;
                                const next = [...prev];
                                next[assistantIdx] = {
                                    ...next[assistantIdx],
                                    content: (next[assistantIdx].content || '') + text
                                };
                                return next;
                            });
                        }

                        for (const sentence of extractCompleteSentences(text)) {
                            enqueueSpeech(sentence);
                        }
                    },

                    onObserver: (data) => {
                        if (!data) return;
                        console.log('👁️ [onObserver] Data recibida:', JSON.stringify(data));
                        if (data.progress != null) setProgress(data.progress);
                        if (data.stance) setStance(data.stance);
                        if (data.body_language) setBodyLanguage(data.body_language);
                        if (data.hint != null) setHint(data.hint);
                        if (data.mastery_signals != null) setMasterySignals(data.mastery_signals);
                    },

                    onScene: (data) => {
                        if (!data) return;
                        console.log('🎬 [onScene] Data recibida:', JSON.stringify(data));
                        if (data.visit_stage) {
                            console.log('🎬 [onScene] Actualizando visit_stage:', data.visit_stage);
                            setVisitStage(data.visit_stage);
                            visitStageFromSceneRef.current = true;
                        }
                        if (Array.isArray(data.scene_moments_shown)) setSceneMomentsShown(data.scene_moments_shown);
                        processSceneMoment(data);
                    },

                    onMessageDone: ({ text, phase } = {}) => {
                        const fullText = (text && text.length >= accumulatedText.length) ? text : accumulatedText;

                        // Fallback: si el backend no manda phase='feedback' pero el contenido
                        // parsea como el JSON de feedback, forzamos la transición igual.
                        const isFeedback = phase === 'feedback' || looksLikeFeedbackJson(fullText);

                        if (isFeedback) {
                            // 1. Mostrar overlay de transición INMEDIATAMENTE
                            setIsFeedbackTransitioning(true);

                            // 2. Cortar TODO el audio: cola pendiente + audio en curso
                            phaseRef.current = 'feedback';
                            audioQueue.length = 0;
                            try {
                                if (ttsAudioRef.current) {
                                    ttsAudioRef.current.pause();
                                    ttsAudioRef.current.currentTime = 0;
                                    ttsAudioRef.current = null;
                                }
                            } catch (_) {}
                            playingState.value = false;
                            setIsTtsPlaying(false);
                            setIsTtsLoading(false);

                            // 3. Quitar la burbuja que muestra el texto crudo del feedback
                            if (assistantIdx != null) {
                                setMessages(prev => prev.filter((_, i) => i !== assistantIdx));
                            }

                            // 4. Setear fase y feedback message
                            setCurrentPhase('feedback');
                            if (fullText) setFeedbackMessage(fullText);
                            return;
                        }

                        // Flush: si quedó una cola sin terminar en punto, mándala a TTS igual
                        const leftover = fullText.slice(stState.lastCut).trim();
                        if (leftover) enqueueSpeech(leftover);
                        stState.buffer = '';
                        stState.lastCut = 0;

                        // Consolidar la burbuja con el texto final y quitar el flag de streaming
                        setMessages(prev => {
                            if (assistantIdx == null || !prev[assistantIdx]) return prev;
                            const next = [...prev];
                            next[assistantIdx] = {
                                ...next[assistantIdx],
                                content: fullText || next[assistantIdx].content,
                                _streaming: false,
                            };
                            return next;
                        });
                    },

                    onDone: (finalPayload) => {
                        if (finalPayload && finalPayload.status === 'success') {
                            updateSimulationState(finalPayload);
                        }
                        setIsFirstMessage(false);
                        setIsInitialized(true);
                    },

                    onError: ({ message, status } = {}) => {
                        console.error('Stream error:', status, message);
                        setMessages(prev => [...prev, {
                            role: 'assistant',
                            content: message || 'Lo siento, no pude procesar tu mensaje. Por favor, intenta de nuevo.'
                        }]);
                    },
                },
                undefined,
                sessionDurationSeconds,
            );
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Error al comunicarse con el servidor. Por favor, verifica tu conexión.'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para manejar envío de mensaje
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isLoading && inputMessage.trim()) {
            sendMessage(inputMessage);
        }
    };

    // Mantener el ref con la última versión de sendMessage para que el effect
    // de silencio (declarado arriba) pueda llamarlo sin forzar dep inestable.
    sendMessageRef.current = sendMessage;

    // Hands-free: detectar silencio y auto-enviar.
    // Cada vez que cambia previewTranscript se resetea el timer; si llega a
    // cero sin nuevos cambios → silencio → enviar.
    // 1500ms (1.5s) — pausa natural al terminar una frase. Si el usuario
    // sigue hablando, el timer se reinicia con cada nueva palabra detectada.
    const SILENCE_THRESHOLD_MS = 1500;
    useEffect(() => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        if (!handsFreeMode || !isLocalSTTActive) return;
        const trimmed = (previewTranscript || '').trim();
        if (!trimmed) return;
        silenceTimerRef.current = setTimeout(() => {
            const text = (previewTranscript || '').trim();
            if (!text) return;
            stopLocalSTT();
            resetLocalSTT();
            sendMessageRef.current?.(text);
        }, SILENCE_THRESHOLD_MS);
        return () => {
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
        };
    }, [handsFreeMode, isLocalSTTActive, previewTranscript, stopLocalSTT, resetLocalSTT]);

    // Función para seleccionar escenario
    const handleScenarioSelect = (scenarioId, scenario) => {
        setSelectedScenario(scenarioId);
        const pj = pickPersonajeEscenario(scenario);
        const pjCfg = pickPersonajeEscenario(ESCENARIOS_CONFIG[scenarioId]);
        setScenarioInfo({
            nombre: scenario.nombre,
            tipo_proyecto: scenario.tipo_proyecto,
            descripcion_breve: scenario.descripcion_breve,
            descripcion_detallada: scenario.descripcion_detallada,
            instrucciones_actuacion: scenario.instrucciones_actuacion,
            nombre_personaje: pj.nombre_personaje ?? pjCfg.nombre_personaje,
            cargo_personaje: pj.cargo_personaje ?? pjCfg.cargo_personaje,
            // Campos Arena: vacíos si el escenario es ambiental.
            hidden_objectives: Array.isArray(scenario.hidden_objectives) ? scenario.hidden_objectives : [],
            walk_away_definition: scenario.walk_away_definition || '',
            relacion_previa: scenario.relacion_previa || '',
            // Configuración de avatar/voz por escenario (opcional). Si vacíos,
            // backend cae a LEMONSLICE_AGENT_ID env y voz default (cedar).
            lemonslice_agent_id: scenario.lemonslice_agent_id || '',
            voice: scenario.voice || '',
            avatar_video_url: scenario.avatar_video_url || '',
        });
        setStoredScenario(scenarioId);
    };

    // Función para volver a selección de escenarios (situaciones)
    const handleBackToScenarios = () => {
        // Apagar mic y limpiar timers de silencio antes de cualquier otra
        // cosa: la instancia de SpeechRecognition sigue activa hasta que
        // llamemos a stop() explícitamente, y el effect de hands-free
        // podría re-arrancarlo si no la cortamos aquí.
        stopLocalSTT();
        resetLocalSTT();
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        // Cortar audio TTS en curso
        if (ttsAudioRef.current) {
            try {
                ttsAudioRef.current.pause();
                ttsAudioRef.current.currentTime = 0;
            } catch (_) {}
            ttsAudioRef.current = null;
        }
        setIsTtsPlaying(false);
        setIsTtsLoading(false);

        setIsSelectingScenario(true);
        setSelectedScenario(null);
        setScenarioInfo(null);
        setStoredScenario(null);
        setThreadId(null);
        setSelectedSkills([]);
        setStoredSkills([]);
        setSelectedLevel(null);
        setStoredLevel(null);
        setMessages([]); // Limpiar mensajes previos
        setIsFirstMessage(true); // Resetear flag de primer mensaje
        setCurrentPhase('recepcion'); // Resetear fase
        setCurrentSkill(''); // Limpiar habilidad actual
        setEvaluatedSkills([]); // Limpiar habilidades evaluadas
        setPendingSkills([]); // Limpiar habilidades pendientes
        setTurnosDesafio(1); // Resetear turnos
        setFeedbackMessage(''); // Limpiar mensaje de feedback
        setIsFeedbackTransitioning(false);
        setHud(null);
        setChallengeLabel('');
        setChallengeIndex(null);
        setChallengePoolDetail(null);
        setMasterySignals(null);
        setCriteriaDetail(null);
        setProgress(0); resetTemperature();
        setExperienceMode('ambiental'); setNegotiationPhase(''); setWalkAwayProximity(0); setWalkAwayPrev(0); setWalkAwayDirection('flat'); setHiddenObjectivesSurfaced({}); setArenaNudges({}); setPivotalMoments([]);
        setStance('');
        setBodyLanguage('');
        setHint('');
        setHintHistory([]);
        setLastSeenHint('');
        setTurnosMinimos(5);
        setTurnosMaximos(7);
        setVisitStage('');
        setSceneMoment('');
        setSceneMomentId('');
        setSceneMomentsShown([]);
        setActiveSceneMoment('');
        renderedSceneIdsRef.current = new Set();
        setInputMessage('');
        setIsInitialized(false); // Resetear estado de inicialización
        setBriefingDismissed(false);
    };

    // Función para limpiar historial
    const handleClearHistory = () => {
        setMessages([]);
        setSelectedScenario(null);
        setScenarioInfo(null);
        setIsFirstMessage(true);
        setCurrentPhase('recepcion');
        setCurrentSkill('');
        setEvaluatedSkills([]);
        setPendingSkills([]);
        setTurnosDesafio(1);
        setFeedbackMessage('');
        setIsFeedbackTransitioning(false);
        setInputMessage('');
        setThreadId(null); // Limpiar threadId para volver al selector
        setStoredScenario(null);
        setSelectedSkills([]);
        setStoredSkills([]);
        setSelectedLevel(null);
        setStoredLevel(null);
        setIsSelectingScenario(true);
        setIsInitialized(false);
        setHud(null);
        setChallengeLabel('');
        setChallengeIndex(null);
        setChallengePoolDetail(null);
        setMasterySignals(null);
        setCriteriaDetail(null);
        setProgress(0); resetTemperature();
        setExperienceMode('ambiental'); setNegotiationPhase(''); setWalkAwayProximity(0); setWalkAwayPrev(0); setWalkAwayDirection('flat'); setHiddenObjectivesSurfaced({}); setArenaNudges({}); setPivotalMoments([]);
        setStance('');
        setBodyLanguage('');
        setHint('');
        setHintHistory([]);
        setLastSeenHint('');
        setTurnosMinimos(5);
        setTurnosMaximos(7);
        setVisitStage('');
        setSceneMoment('');
        setSceneMomentId('');
        setSceneMomentsShown([]);
        setActiveSceneMoment('');
        renderedSceneIdsRef.current = new Set();
        setBriefingDismissed(false);
        clearLogs();
    };

    // Función para generar nuevo thread
    const handleNewThread = () => {
        const newThreadId = `thread-${uuidv4().slice(0, 8)}`;
        setThreadId(newThreadId);
        handleClearHistory();
    };

    // Función para cargar mensajes de un thread específico
    const handleLoadMessages = async (targetThreadId) => {
        if (targetThreadId && targetThreadId.trim() !== '') {
            console.log('🔄 handleLoadMessages iniciado para thread:', targetThreadId);
            
            // Limpiar solo el estado de la simulación, NO el escenario
            // El escenario se cargará desde el backend si existe
            setMessages([]);
            setCurrentPhase('recepcion');
            setCurrentSkill('');
            setEvaluatedSkills([]);
            setPendingSkills([]);
            setTurnosDesafio(1);
            setFeedbackMessage('');
            setHud(null);
            setChallengeLabel('');
            setChallengeIndex(null);
            setChallengePoolDetail(null);
            setMasterySignals(null);
            setCriteriaDetail(null);
            setProgress(0); resetTemperature();
        setExperienceMode('ambiental'); setNegotiationPhase(''); setWalkAwayProximity(0); setWalkAwayPrev(0); setWalkAwayDirection('flat'); setHiddenObjectivesSurfaced({}); setArenaNudges({}); setPivotalMoments([]);
            setStance('');
            setBodyLanguage('');
            setHint('');
            setHintHistory([]);
            setLastSeenHint('');
            setTurnosMinimos(5);
            setTurnosMaximos(7);
            setVisitStage('');
            setSceneMoment('');
            setSceneMomentId('');
            setSceneMomentsShown([]);
            setActiveSceneMoment('');
            renderedSceneIdsRef.current = new Set();
            setInputMessage('');
            setIsFirstMessage(true);
            setIsInitialized(false);
            setBriefingDismissed(false);

            // Actualizar el thread ID (esto activará el useEffect de carga)
            console.log('🔄 Actualizando threadId a:', targetThreadId);
            setThreadId(targetThreadId);
            
            console.log('✅ Estado de simulación limpiado, el useEffect cargará TODO desde el backend');
        } else {
            console.log('⚠️ Thread ID inválido:', targetThreadId);
        }
    };

    // Scroll automático al final de los mensajes
    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, isLoading]);

    // Cerrar menú de Settings al clickear fuera o presionar Escape.
    useEffect(() => {
        if (!settingsMenuOpen) return;
        const handleClickOutside = (e) => {
            if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target)) {
                setSettingsMenuOpen(false);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') setSettingsMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [settingsMenuOpen]);

    // Scroll al fondo + foco en el textarea cuando se abre el panel de chat.
    // El campo de texto vive dentro de este panel desde que pasamos a UI
    // voice-first; al abrirlo, el usuario quiere escribir, así que enfocamos
    // directo para evitar un click extra.
    useEffect(() => {
        if (isChatOpen) {
            setTimeout(() => {
                const container = chatContainerRef.current;
                if (container) container.scrollTop = container.scrollHeight;
                inputRef.current?.focus();
            }, 0);
        }
    }, [isChatOpen]);

    // Sincronizar phaseRef con currentPhase para closures de streaming
    useEffect(() => {
        phaseRef.current = currentPhase;
    }, [currentPhase]);

    // Detener sesión de voz automáticamente cuando se entra en fase de feedback
    useEffect(() => {
        if (isInFeedbackPhase() && isLocalSTTActive) {
            console.log('🛑 Entrando en fase de feedback - deteniendo captura local');
            stopLocalSTT();
            resetLocalSTT();
        }
    }, [currentPhase, isLocalSTTActive, stopLocalSTT, resetLocalSTT]);

    // Parar audio TTS al entrar en fase de feedback
    useEffect(() => {
        if (isInFeedbackPhase()) {
            try {
                if (ttsAudioRef.current) {
                    ttsAudioRef.current.pause();
                    ttsAudioRef.current.currentTime = 0;
                    ttsAudioRef.current = null;
                }
            } catch (_) {}
            setIsTtsPlaying(false);
            setIsTtsLoading(false);
        }
    }, [currentPhase]);

    // Mostrar hint overlay cuando llega una pista nueva y acumularla en historial
    useEffect(() => {
        if (hint && hint !== lastSeenHint) {
            setLastSeenHint(hint);
            setHintOverlayVisible(true);
            setHintHistory(prev => [
                ...prev.map(h => ({ ...h, active: false })),
                { text: hint, timestamp: Date.now(), active: true }
            ]);
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
            hintTimerRef.current = setTimeout(() => setHintOverlayVisible(false), 10000);
        }
    }, [hint, lastSeenHint]);

    // Reproducir automáticamente el feedback cuando se recibe
    // (deshabilitado: el usuario elige cuándo escucharlo con el botón "Escuchar")


    // Cargar mensajes previos al iniciar o cuando cambie el thread ID
    useEffect(() => {
        if (threadId && threadId.trim() !== '') {
            console.log('🔄 useEffect detectó cambio en threadId:', threadId);
            const loadMessages = async () => {
                // Usamos storedScenario (hidratado sincrónicamente desde localStorage)
                // en vez de selectedScenario, que aún puede ser null en el primer render.
                // Esto evita que al recargar con un thread vacío se muestre el selector
                // y el usuario acabe regenerando el thread_id por accidente.
                const hasScenario = !!(storedScenario && ESCENARIOS[storedScenario]);
                try {
                    const success = await loadPreviousMessages(threadId);
                    if (success) {
                        console.log('✅ Thread cargado exitosamente con todos sus datos');
                    } else {
                        console.log('⚠️ No se pudieron cargar mensajes del thread');
                        console.log('ℹ️ Esto puede ser porque:');
                        console.log('   - El thread no existe en el backend');
                        console.log('   - El thread no tiene mensajes previos');
                        console.log('   - Hubo un error en el backend');

                        if (hasScenario) {
                            console.log('✅ No hay mensajes previos pero hay escenario guardado. Habilitando simulación...');
                            setIsInitialized(true);
                        } else {
                            console.log('ℹ️ No hay escenario guardado, mostrando selector...');
                        }
                    }
                } catch (error) {
                    console.error('❌ Error en useEffect loadMessages:', error);
                    if (hasScenario) {
                        console.log('✅ Error en carga pero hay escenario guardado. Habilitando simulación...');
                        setIsInitialized(true);
                    }
                }
            };
            loadMessages();
        }
    }, [threadId]); // ✅ SOLO threadId como dependencia

    // Cargar escenario guardado.
    // IMPORTANTE: dependemos también de `content` porque ESCENARIOS sale de
    // useContent() y puede no estar listo en el primer render. Sin esta
    // dependencia, al recargar con un thread_id válido pero antes de que
    // termine de cargar el contenido, selectedScenario se quedaba en null
    // para siempre y el gatekeeper mostraba el selector de escenario.
    useEffect(() => {
        if (storedScenario && ESCENARIOS[storedScenario]) {
            const scenario = ESCENARIOS[storedScenario];
            setSelectedScenario(storedScenario);
            const pj = pickPersonajeEscenario(scenario);
            setScenarioInfo({
                nombre: scenario.nombre,
                tipo_proyecto: scenario.tipo_proyecto,
                descripcion_breve: scenario.descripcion_breve,
                descripcion_detallada: scenario.descripcion_detallada,
                instrucciones_actuacion: scenario.instrucciones_actuacion,
                nombre_personaje: pj.nombre_personaje,
                cargo_personaje: pj.cargo_personaje,
                hidden_objectives: Array.isArray(scenario.hidden_objectives) ? scenario.hidden_objectives : [],
                walk_away_definition: scenario.walk_away_definition || '',
                relacion_previa: scenario.relacion_previa || '',
                lemonslice_agent_id: scenario.lemonslice_agent_id || '',
                voice: scenario.voice || '',
                avatar_video_url: scenario.avatar_video_url || '',
            });
            setIsInitialized(true);
        }
    }, [storedScenario, content, selectedLanguage]);

    // Cargar habilidades guardadas (sólo si siguen existiendo en el bundle actual,
    // por si el admin renombró/eliminó la skill desde la última visita).
    useEffect(() => {
        if (!storedSkills || storedSkills.length === 0) return;
        const available = contentSlice?.skills || {};
        const valid = storedSkills.filter(k => available[k]);
        if (valid.length > 0) {
            setSelectedSkills(valid);
        } else {
            const fallbackKey = Object.keys(available)[0];
            if (fallbackKey) {
                setSelectedSkills([fallbackKey]);
                setStoredSkills([fallbackKey]);
            }
        }
    }, [storedSkills, contentSlice, setStoredSkills]);

    // Cargar nivel guardado
    useEffect(() => {
        if (storedLevel) {
            setSelectedLevel(storedLevel);
        }
    }, [storedLevel]);

    // Sin tutorial Joyride en la versión lite arcade.

    

    // Si se está mostrando la pantalla de introducción
    if (isLoadingContent) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-3 p-4">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#22d3ee' }} />
                    <p className="text-sm text-slate-300">{t('loadingConfig')}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Guardia anti-flash: si hay thread + escenario guardados en localStorage
    // pero aún no se ha hidratado al estado, mostramos un loader.
    if (threadId && storedScenario && !selectedScenario) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-3 p-4">
                    <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
                    <p className="text-sm font-mono tracking-wider text-neon-cyan/70 uppercase">{t('restoringConversation')}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Pantalla de setup arcade: 3 situaciones de negociación.
    if (!isInitialized || !selectedScenario || isSelectingScenario) {
        const handleSelectSituation = (situationId) => {
            const scenario = ESCENARIOS[situationId];
            if (!scenario) return;
            handleScenarioSelect(situationId, scenario);
            setSelectedLevel(1);
            setStoredLevel(1);
            // El frontend lite no muestra SkillSelector: usamos la primera (y única)
            // skill del bundle publicado. Contrato del blob arena: una sola skill,
            // misma `skill_key` en es/en (ej. 'NEGOCIACIÓN' / slug 'negociacion').
            const skillKey = Object.keys(contentSlice?.skills || {})[0] || 'NEGOCIACIÓN';
            setSelectedSkills([skillKey]);
            setStoredSkills([skillKey]);
        };
        const handleStart = () => {
            if (!selectedScenario) return;
            const sameScenarioAsBefore = storedScenario === selectedScenario && threadId;
            if (!sameScenarioAsBefore) {
                setThreadId(`thread-${uuidv4().slice(0, 8)}`);
            }
            setIsSelectingScenario(false);
            setIsInitialized(true);
            resetTemperature();
        };
        return (
            <SetupScreen
                scenarios={ESCENARIOS}
                selectedScenario={selectedScenario}
                onSelectSituation={handleSelectSituation}
                onStart={handleStart}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        );
    }

    const getStanceLabel = (s) => {
        const labels = { supportive: t('supportive'), neutral: t('neutral'), resistant: t('resistant'), challenging: t('challenging'), open: t('open'), closed: t('closed'), skeptical: t('skeptical'), conciliatory: t('conciliatory'), firm: t('firm') };
        return labels[s] || s;
    };
    const getStanceColor = (s) => {
        if (s === 'supportive' || s === 'open' || s === 'conciliatory') return 'bg-neon-lime/10 text-neon-lime border-neon-lime/30';
        if (s === 'neutral') return 'bg-neon-amber/10 text-neon-amber border-neon-amber/30';
        return 'bg-red-50 text-red-400 border-red-500/30';
    };

    const stopTTS = () => {
        try {
            if (ttsAudioRef.current) {
                ttsAudioRef.current.pause();
                ttsAudioRef.current.currentTime = 0;
                ttsAudioRef.current = null;
            }
        } catch (_) {}
        setIsTtsPlaying(false);
        setCurrentSpokenSentence('');
        addLog('⏹️ Audio detenido', 'info');
    };

    // Nombres de habilidades seleccionadas (para FeedbackView)
    const HABILIDADES_DISPONIBLES_APP = contentSlice?.skills || {};
    const selectedSkillNames = selectedSkills.map(k =>
        HABILIDADES_DISPONIBLES_APP[k]?.nombre || k
    );

    // Overlay de transición a feedback: tapa la pantalla mientras se procesa
    if (isFeedbackTransitioning && !isFeedbackCompleted()) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-4 p-6 animate-fade-in">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#22d3ee' }} />
                    <p className="text-sm font-medium text-slate-200">{t('generatingFeedback')}</p>
                    <p className="text-xs text-slate-400">{t('pleaseWait') || 'Un momento por favor...'}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Mostrar FeedbackView cuando el feedback ya está listo
    if (isFeedbackCompleted() && feedbackMessage) {
        return (
            <FeedbackView
                feedbackMessage={feedbackMessage}
                scenarioInfo={scenarioInfo}
                selectedSkills={selectedSkills}
                skillNames={selectedSkillNames}
                progress={progress}
                onRestart={handleBackToScenarios}
                isTtsPlaying={isTtsPlaying}
                isTtsLoading={isTtsLoading}
                onPlayTTS={playTTS}
                onStopTTS={stopTTS}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        );
    }

    return (
        <BackgroundStack className="flex h-screen" sidebarExpanded={false}>
            <div className="flex min-h-0 w-full min-w-0 flex-1 items-stretch gap-2.5 p-2.5 pl-2.5 pr-1">
            <div className="sim-main-elevated flex min-h-0 min-w-0 flex-1 flex-col relative overflow-hidden">
            {/* ─── Zona principal: Audio Hero (Capa 06) ─── */}

                {/* Header */}
                <div className={`absolute top-0 left-0 right-0 ${settingsMenuOpen ? 'z-40' : 'z-10'}`} style={{ background: 'rgba(10, 10, 20, 0.92)', borderBottom: '1px solid rgba(34, 211, 238, 0.18)', backdropFilter: 'blur(10px)' }}>
                    <div className="flex items-center justify-between px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#22d3ee' }}>
                                <span className="text-white font-bold text-xs">S</span>
                            </div>
                            <span className="font-semibold text-white text-sm">SimulationAI</span>
                            <span className="text-slate-400 text-sm">|</span>
                            <span className="text-slate-300 text-sm">{t('idbAse')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1.5 text-[10px] font-mono tracking-[0.2em] uppercase font-bold px-3 py-1 rounded-full border border-neon-lime/50 text-neon-lime bg-neon-lime/10" style={{ textShadow: '0 0 6px rgba(163, 230, 53, 0.6)' }}>
                                <span className="w-1.5 h-1.5 rounded-full bg-neon-lime" style={{ boxShadow: '0 0 6px #a3e635, 0 0 12px #a3e635' }} />
                                {t('live')}
                            </span>
                            <LanguageSelector
                                selectedLanguage={selectedLanguage}
                                onLanguageChange={setSelectedLanguage}
                                disabled={isLoading}
                            />
                            <button
                                onClick={handleBackToScenarios}
                                className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-lg hover:bg-bg-700/50 transition-colors"
                                title={t('restart')}
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {t('restart')}
                            </button>
                            <button
                                data-tour="header-help"
                                onClick={() => setShowHelpModal(true)}
                                className="p-2 text-slate-400 hover:text-slate-200 rounded-lg transition-colors"
                            >
                                <HelpCircle className="w-4 h-4" />
                            </button>
                            {/* Menú de Settings: contiene toggle de avatar (si
                                ambos modos están habilitados vía env) y enlace
                                al admin. Se cierra al clickear fuera o Escape. */}
                            <div ref={settingsMenuRef} className="relative z-50">
                                <button
                                    onClick={() => setSettingsMenuOpen(o => !o)}
                                    className={`p-2 rounded-lg transition-colors ${
                                        settingsMenuOpen
                                            ? 'text-neon-cyan bg-bg-700/50'
                                            : 'text-slate-400 hover:text-slate-200 hover:bg-bg-700/30'
                                    }`}
                                    title="Configuración"
                                    aria-haspopup="menu"
                                    aria-expanded={settingsMenuOpen}
                                >
                                    <Settings className="w-4 h-4" />
                                </button>
                                {settingsMenuOpen && (
                                    <div
                                        role="menu"
                                        className="absolute right-0 top-full mt-2 w-60 rounded-xl z-50 animate-fade-in overflow-hidden"
                                        style={{
                                            background: 'rgba(10, 10, 20, 0.97)',
                                            border: '1px solid rgba(34, 211, 238, 0.3)',
                                            boxShadow: '0 0 24px rgba(34, 211, 238, 0.15), 0 12px 32px rgba(0, 0, 0, 0.5)',
                                            backdropFilter: 'blur(12px)',
                                        }}
                                    >
                                        {canToggleAvatar && (
                                            <>
                                                <div className="px-3 pt-3 pb-1.5">
                                                    <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500">
                                                        Modo de avatar
                                                    </span>
                                                </div>
                                                <button
                                                    role="menuitem"
                                                    onClick={() => {
                                                        setAvatarModePref(avatarModePref === 'lemonslice' ? 'video' : 'lemonslice');
                                                        setSettingsMenuOpen(false);
                                                    }}
                                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-slate-200 hover:bg-bg-700/50 transition-colors"
                                                >
                                                    {avatarModePref === 'lemonslice' ? (
                                                        <>
                                                            <Sparkles className="w-3.5 h-3.5 text-neon-cyan shrink-0" />
                                                            <span className="flex-1 text-left">Lemon Slice <span className="text-slate-500">(lipsync)</span></span>
                                                            <span className="text-[9px] font-mono uppercase tracking-wider text-neon-cyan/70">activo</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Film className="w-3.5 h-3.5 text-neon-cyan shrink-0" />
                                                            <span className="flex-1 text-left">Video loop <span className="text-slate-500">(mp4)</span></span>
                                                            <span className="text-[9px] font-mono uppercase tracking-wider text-neon-cyan/70">activo</span>
                                                        </>
                                                    )}
                                                </button>
                                                <div className="border-t border-neon-cyan/10 my-1" />
                                            </>
                                        )}
                                        <Link
                                            to="/admin"
                                            role="menuitem"
                                            onClick={() => setSettingsMenuOpen(false)}
                                            className="flex items-center gap-2.5 px-3 py-2.5 text-xs text-slate-200 hover:bg-bg-700/50 transition-colors"
                                        >
                                            <Settings className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                            <span>Gestor de contenido</span>
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* ── Termómetro horizontal arcade — degradado frío → caliente ── */}
                    {(() => {
                        const t100 = Math.max(0, Math.min(100, Math.round(temperature)));
                        // Color del valor actual interpolado entre 3 zonas
                        // Paleta del termómetro (Warmth-medium-cold).
                        // El HOT usa una variante un poco más profunda del
                        // coral FCA08B para que se distinga de WARM (FDB285)
                        // contra el fondo oscuro — los pasteles tienden a
                        // lavarse y a fundirse entre sí.
                        const currentColor =
                            t100 >= 67 ? '#A3EDA2' :   // successful — green
                            t100 >= 50 ? '#B7E389' :   // light green
                            t100 >= 34 ? '#F6D37C' :   // mid — soft yellow
                            t100 >= 17 ? '#FCA08B' :   // coral
                                         '#E87765';    // unsuccessful — deeper coral
                        const glow = currentColor + '99';

                        return (
                            <div className="px-6 pb-3.5 pt-1">
                                <div className="flex items-center gap-4 font-mono">
                                    {/* Etiqueta TEMP grande */}
                                    <div className="flex flex-col items-start shrink-0">
                                        <span className="text-[9px] tracking-[0.3em] uppercase text-neon-cyan/60 leading-none">
                                            {t('temperature')}
                                        </span>
                                        <span
                                            className="text-2xl font-bold tabular-nums leading-none mt-1"
                                            style={{ color: currentColor, textShadow: `0 0 10px ${glow}, 0 0 20px ${glow}` }}
                                        >
                                            {t100}
                                            <span className="text-xs ml-1 opacity-60">/100</span>
                                        </span>
                                    </div>

                                    {/* Barra termómetro horizontal */}
                                    <div className="flex-1 relative">
                                        {/* Etiquetas COLD/WARM/HOT encima */}
                                        <div className="flex justify-between items-end mb-1 px-0.5">
                                            <span className="text-[9px] tracking-[0.25em] uppercase font-bold" style={{ color: '#E87765', opacity: t100 < 34 ? 1 : 0.4, textShadow: t100 < 34 ? '0 0 6px rgba(232,119,101,0.7)' : 'none' }}>
                                                UNSUCCESSFUL
                                            </span>
                                            <span className="text-[9px] tracking-[0.25em] uppercase font-bold" style={{ color: '#F6D37C', opacity: t100 >= 34 && t100 < 67 ? 1 : 0.4, textShadow: t100 >= 34 && t100 < 67 ? '0 0 6px rgba(246,211,124,0.7)' : 'none' }}>
                                                WARM
                                            </span>
                                            <span className="text-[9px] tracking-[0.25em] uppercase font-bold" style={{ color: '#A3EDA2', opacity: t100 >= 67 ? 1 : 0.4, textShadow: t100 >= 67 ? '0 0 6px rgba(163,237,162,0.7)' : 'none' }}>
                                                SUCCESSFUL
                                            </span>
                                        </div>

                                        {/* Barra: fondo gradiente FIJO frío→caliente */}
                                        <div
                                            className="relative h-4 rounded-full overflow-visible"
                                            style={{
                                                background: 'linear-gradient(90deg, #E87765 0%, #FCA08B 18%, #FDB285 35%, #F6D37C 55%, #B7E389 78%, #A3EDA2 100%)',
                                                border: '1px solid rgba(34, 211, 238, 0.3)',
                                                boxShadow: '0 0 12px rgba(34, 211, 238, 0.15), inset 0 1px 2px rgba(0, 0, 0, 0.5)',
                                            }}
                                        >
                                            {/* Velo que apaga la zona aún no alcanzada. Opacidad
                                                reducida para que los tonos pastel del lado hot
                                                no se laven contra el fondo oscuro.
                                                Transition 800ms con ease-out: los saltos del
                                                termómetro Arena ahora son discretos (1/N por objetivo),
                                                conviene una animación más larga y suave. */}
                                            <div
                                                className="absolute top-0 right-0 bottom-0 rounded-r-full"
                                                style={{
                                                    left: `${t100}%`,
                                                    background: 'linear-gradient(90deg, rgba(5, 5, 10, 0.0) 0%, rgba(5, 5, 10, 0.35) 18%, rgba(5, 5, 10, 0.45) 100%)',
                                                    transition: 'left 800ms cubic-bezier(0.22, 1, 0.36, 1)',
                                                }}
                                            />

                                            {/* Marcas internas */}
                                            {[25, 50, 75].map((m) => (
                                                <div
                                                    key={m}
                                                    className="absolute top-0 bottom-0 w-px bg-white/15"
                                                    style={{ left: `${m}%` }}
                                                />
                                            ))}

                                            {/* Marcador / aguja del valor actual (incluye progreso volátil del 0.5) */}
                                            <div
                                                className="absolute top-1/2 -translate-y-1/2"
                                                style={{
                                                    left: `${t100}%`,
                                                    transform: 'translate(-50%, -50%)',
                                                    transition: 'left 800ms cubic-bezier(0.22, 1, 0.36, 1)',
                                                }}
                                            >
                                                <div
                                                    className="w-3 h-7 rounded-sm"
                                                    style={{
                                                        background: '#fff',
                                                        boxShadow: `0 0 8px ${currentColor}, 0 0 16px ${glow}, 0 0 24px ${glow}`,
                                                        border: `2px solid ${currentColor}`,
                                                    }}
                                                />
                                            </div>

                                            {/* Marcador secundario "ratchet floor": muestra el progreso
                                                consolidado (objetivos plenamente destapados, state=1).
                                                Solo aparece en Arena y cuando difiere del marcador principal,
                                                para señalar el "piso" de logro permanente vs lo que está en
                                                juego ahora mismo (estados 0.5 que pueden retroceder). */}
                                            {experienceMode === 'arena' && (() => {
                                                const total = Object.keys(hiddenObjectivesSurfaced).length;
                                                if (total === 0) return null;
                                                const fully = Object.values(hiddenObjectivesSurfaced)
                                                    .filter(v => (typeof v === 'number' ? v : (v ? 1 : 0)) >= 1).length;
                                                const floorPct = Math.round((fully / total) * 100);
                                                if (floorPct === 0 || floorPct >= t100) return null;
                                                return (
                                                    <div
                                                        className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                                                        style={{
                                                            left: `${floorPct}%`,
                                                            transform: 'translate(-50%, -50%)',
                                                            transition: 'left 800ms cubic-bezier(0.22, 1, 0.36, 1)',
                                                        }}
                                                        aria-label={`Logro consolidado ${fully}/${total}`}
                                                    >
                                                        <div
                                                            className="w-1 h-5 rounded-sm"
                                                            style={{
                                                                background: 'rgba(255, 255, 255, 0.55)',
                                                                boxShadow: '0 0 4px rgba(255, 255, 255, 0.6)',
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Personaje + Audio Hero — zona scrollable entre header e input */}
                <div className="absolute inset-0 overflow-y-auto pt-[80px] pb-16 z-0">
                <div className="flex flex-col items-center max-w-2xl w-full px-6 mx-auto min-h-full justify-center py-6">



                    {/* Avatar */}
                    <div data-tour="avatar" className="relative mb-4">
                        {/* Hint overlay del coach (fade-in/fade-out) */}
                        {hint && (
                            <div
                                className="absolute z-20 right-full mr-4 top-0 w-64 bg-bg-800/95 backdrop-blur-sm rounded-2xl border border-neon-amber/40 p-4 transition-all duration-500 ease-in-out"
                                style={{
                                    minWidth: '220px',
                                    opacity: hintOverlayVisible ? 1 : 0,
                                    transform: hintOverlayVisible ? 'translateY(0)' : 'translateY(-8px)',
                                    pointerEvents: hintOverlayVisible ? 'auto' : 'none',
                                    boxShadow: '0 0 12px rgba(251, 191, 36, 0.35), 0 0 24px rgba(251, 191, 36, 0.15)',
                                }}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Lightbulb className="w-4 h-4 text-neon-amber shrink-0" />
                                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-neon-amber">{t('coachHint')}</span>
                                    <button
                                        onClick={() => { setHintOverlayVisible(false); if (hintTimerRef.current) clearTimeout(hintTimerRef.current); }}
                                        className="ml-auto text-slate-500 hover:text-neon-amber transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                                <p className="text-sm text-slate-200 leading-relaxed">{hint}</p>
                            </div>
                        )}

                        {isLemonActive ? (
                            <LemonSliceAvatar
                                ref={avatarRef}
                                isSpeaking={isTtsPlaying}
                                isListening={isLocalSTTActive}
                                isThinking={isLoading || isTtsLoading}
                                agentId={scenarioInfo?.lemonslice_agent_id || null}
                                onReady={handleAvatarReady}
                            />
                        ) : isVideoActive ? (
                            <VideoAvatar
                                ref={avatarRef}
                                isSpeaking={isTtsPlaying}
                                isListening={isLocalSTTActive}
                                isThinking={isLoading || isTtsLoading}
                                videoUrl={scenarioInfo?.avatar_video_url || ''}
                                onReady={handleAvatarReady}
                            />
                        ) : (
                            <AnimatedAvatar
                                isSpeaking={isTtsPlaying}
                                isListening={isLocalSTTActive}
                                isThinking={isLoading || isTtsLoading}
                            />
                        )}

                    </div>

                    {/* Nombre y rol */}
                    <h2 className="text-2xl font-bold text-white mb-0.5 tracking-tight">
                        {scenarioInfo?.nombre_personaje || t('yourInterlocutor')}
                    </h2>
                    <p className="text-sm text-slate-300 mb-8">
                        {scenarioInfo?.cargo_personaje
                            ? scenarioInfo.cargo_personaje.replace('·', '•')
                            : scenarioInfo?.tipo_proyecto || ''}
                    </p>

                    {/* Subtitulos tipo cine: en flujo, justo debajo del cargo.
                        Una frase a la vez (avatar) o preview del STT (usuario),
                        con identificacion minima arriba. Reserva min-height para
                        que el layout no salte cuando aparece/desaparece. */}
                    {subtitlesEnabled && (() => {
                        const userPreview = (previewTranscript || '').trim();
                        const showUser = isLocalSTTActive && userPreview.length > 0;
                        const showAssistant = !showUser && isTtsPlaying && currentSpokenSentence;
                        const visible = showUser || showAssistant;
                        const speaker = showUser
                            ? t('userLabel')
                            : (scenarioInfo?.nombre_personaje || '').toUpperCase();
                        const text = showUser ? userPreview : currentSpokenSentence;
                        const speakerClass = showUser
                            ? 'text-neon-cyan/80'
                            : 'text-neon-magenta/80';
                        return (
                            <div
                                className={`w-full max-w-xl px-4 mb-4 text-center transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{ minHeight: '3.25rem' }}
                                aria-live="polite"
                            >
                                {speaker && (
                                    <div className={`text-[10px] font-mono tracking-[0.25em] font-bold mb-1 ${speakerClass}`}>
                                        {`— ${speaker}`}
                                    </div>
                                )}
                                <p
                                    className="text-base md:text-lg font-semibold text-white leading-snug"
                                    style={{ textShadow: '0 2px 8px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.7)' }}
                                >
                                    {text || ' '}
                                </p>
                            </div>
                        );
                    })()}

                    {/* Status badge */}
                    <div className="mb-6">
                        {isTtsPlaying ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-neon-lime/10 text-neon-lime border border-neon-lime/30">
                                <span className="w-1.5 h-1.5 rounded-full bg-neon-lime/100 animate-pulse" />
                                {t('speaking')}
                            </span>
                        ) : isLocalSTTActive ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-neon-lime/10 text-neon-lime border border-neon-lime/30">
                                <span className="w-1.5 h-1.5 rounded-full bg-neon-lime/100" />
                                {t('listening')}
                            </span>
                        ) : isLoading ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-bg-700/50 text-slate-300 border border-neon-cyan/15">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {t('thinking')}
                            </span>
                        ) : null}
                    </div>

                    {/* Etiquetas de contexto */}
                    {(stance || visitStage || activeSceneMoment || bodyLanguage) && (() => {
                        const hideStance = currentPhase === 'recepcion';
                        const displayStance = hideStance ? '' : stance;
                        const displayVisitStage = visitStage;
                        const displaySceneMoment = activeSceneMoment;
                        const displayBodyLanguage = ''; // oculto: lógica de estado intacta por si se quiere restaurar
                        return (
                            <div data-tour="avatar-labels" className="w-full flex flex-col items-center">
                                {(displayStance || displayVisitStage) && (
                                    <div className="flex items-center gap-2 mb-3 flex-wrap justify-center">
                                        {displayStance && (
                                            <span className={`text-[11px] font-medium px-4 py-1.5 rounded-full border ${getStanceColor(displayStance)}`}>
                                                {getStanceLabel(displayStance)}
                                            </span>
                                        )}
                                        {displayVisitStage && (
                                            <span className="text-[11px] font-medium px-4 py-1.5 rounded-full border border-neon-cyan/25 bg-bg-700/50 text-slate-200 flex items-center gap-1.5">
                                                <MapPin className="w-3 h-3" />
                                                {getVisitStageLabel(displayVisitStage)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {displayBodyLanguage && (
                                    <div data-tour="body-language" className="w-full rounded-xl px-5 py-3.5 mb-4" style={{ background: 'rgba(18, 18, 31, 0.7)', border: '1px solid rgba(232, 121, 249, 0.25)' }}>
                                        <div className="flex items-start gap-2.5">
                                            <Eye className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                            <p className="text-[13px] text-slate-200 italic leading-relaxed">{displayBodyLanguage}</p>
                                        </div>
                                    </div>
                                )}
                                {displaySceneMoment && (
                                    <div className="w-full mb-5 rounded-xl border border-neon-amber/30 bg-neon-amber/10 px-5 py-3 animate-fade-in">
                                        <div className="flex items-start gap-2.5">
                                            <Film className="w-3.5 h-3.5 text-neon-amber mt-0.5 shrink-0" />
                                            <p className="text-[12px] text-neon-amber italic leading-relaxed">{displaySceneMoment}</p>
                                            {activeSceneMoment && (
                                                <button onClick={() => { if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current); setActiveSceneMoment(''); }} className="ml-auto p-0.5 text-neon-amber/80 hover:text-neon-amber transition-colors shrink-0" aria-label="Cerrar">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* ─── Bloque Arena: nudges + objetivos ocultos + fase ─── */}
                    {experienceMode === 'arena' && (
                        <div className="w-full mb-4 space-y-2">
                            {negotiationPhase && (
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-neon-cyan/70">
                                        {t('negotiationPhase') || 'Phase'}:
                                    </span>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-neon-cyan">
                                        {negotiationPhase.replace(/_/g, ' ')}
                                    </span>
                                    {walkAwayDirection !== 'flat' && (
                                        <span className={`text-[11px] font-bold ${walkAwayDirection === 'up' ? 'text-orange-400' : 'text-cyan-400'}`}>
                                            {walkAwayDirection === 'up' ? '▲' : '▼'}
                                        </span>
                                    )}
                                </div>
                            )}

                            {Object.keys(hiddenObjectivesSurfaced).length > 0 && (
                                <div className="flex flex-wrap items-center justify-center gap-1.5">
                                    {Object.entries(hiddenObjectivesSurfaced).map(([key, raw]) => {
                                        // Estado por objetivo (backend ahora devuelve float 0 | 0.5 | 1):
                                        //   1   → destapado (verde, ratchet)
                                        //   0.5 → en juego este turno (ámbar, volátil)
                                        //   0   → no tocado (gris)
                                        const v = typeof raw === 'number' ? raw : (raw ? 1 : 0);
                                        let cls, icon;
                                        if (v >= 1) {
                                            cls = 'bg-neon-lime/15 text-neon-lime border-neon-lime/40';
                                            icon = '✓';
                                        } else if (v >= 0.5) {
                                            cls = 'bg-amber-400/15 text-amber-300 border-amber-400/40';
                                            icon = '◐';
                                        } else {
                                            cls = 'bg-bg-700/50 text-slate-500 border-slate-600/40';
                                            icon = '○';
                                        }
                                        return (
                                            <span
                                                key={key}
                                                className={`text-[10px] px-2 py-0.5 rounded-full border font-mono tracking-wide ${cls}`}
                                            >
                                                {icon} {key.replace(/_/g, ' ')}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {(arenaNudges.good_move || arenaNudges.watch_out || arenaNudges.move_available) && (
                                <div className="space-y-1.5">
                                    {arenaNudges.good_move && (
                                        <div className="rounded-lg px-3 py-2 bg-neon-lime/10 border border-neon-lime/30">
                                            <p className="text-[12px] text-neon-lime font-medium">✓ {arenaNudges.good_move}</p>
                                            {arenaNudges.good_move_why && (
                                                <p className="text-[11px] text-neon-lime/70 mt-0.5">{arenaNudges.good_move_why}</p>
                                            )}
                                        </div>
                                    )}
                                    {arenaNudges.watch_out && (
                                        <div className="rounded-lg px-3 py-2 bg-orange-500/10 border border-orange-500/30">
                                            <p className="text-[12px] text-orange-300 font-medium">⚠ {arenaNudges.watch_out}</p>
                                            {arenaNudges.watch_out_risk && (
                                                <p className="text-[11px] text-orange-300/70 mt-0.5">{arenaNudges.watch_out_risk}</p>
                                            )}
                                        </div>
                                    )}
                                    {arenaNudges.move_available && (
                                        <div className="rounded-lg px-3 py-2 bg-neon-cyan/10 border border-neon-cyan/30">
                                            <p className="text-[12px] text-neon-cyan font-medium">→ {arenaNudges.move_available}</p>
                                            {arenaNudges.move_available_why && (
                                                <p className="text-[11px] text-neon-cyan/70 mt-0.5">{arenaNudges.move_available_why}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Badge REC: solo visible cuando el mic está activo en
                        modo hands-free, para avisar claramente al usuario. */}
                    {isLocalSTTActive && (
                        <div className="mb-2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/50 shadow-[0_0_16px_rgba(239,68,68,0.4)]">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-red-300">
                                {t('recording')}
                            </span>
                        </div>
                    )}

                    {/* Botón de micrófono con anillo orbital */}
                    <div className="relative mt-4 mb-6">
                        {/* Halo exterior que respira */}
                        {!isInFeedbackPhase() && !isFeedbackCompleted() && <div className="mic-halo" />}
                        {/* Anillo orbital giratorio */}
                        {!isInFeedbackPhase() && !isFeedbackCompleted() && <div className="mic-orbit-ring" />}
                        {(() => {
                            const micDisabled = isInFeedbackPhase() || isFeedbackCompleted() || !isAvatarReady || !briefingDismissed || isTtsPlaying || isTtsLoading || isLoading;
                            // 3 estados de la convención voice-first:
                            //  - disabled: gris + icono tachado (MicOff)
                            //  - recording (isLocalSTTActive): rojo "en vivo" + Mic
                            //  - idle habilitado: verde sólido + Mic
                            return (
                                <button
                                    data-tour="mic"
                                    onClick={toggleMic}
                                    disabled={micDisabled}
                                    className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                                        micDisabled
                                            ? 'opacity-50 cursor-not-allowed'
                                            : isLocalSTTActive
                                                ? 'shadow-[0_0_40px_rgba(239,68,68,0.45)]'
                                                : 'shadow-[0_0_30px_rgba(34,197,94,0.35)] hover:scale-105'
                                    }`}
                                    style={{
                                        background: micDisabled
                                            ? 'linear-gradient(145deg, #94a3b8, #64748b)'
                                            : isLocalSTTActive
                                                ? 'linear-gradient(145deg, #ef4444, #dc2626)'
                                                : 'linear-gradient(145deg, #22c55e, #16a34a)',
                                    }}
                                >
                                    {micDisabled ? (
                                        <MicOff className="w-7 h-7 text-white" />
                                    ) : (
                                        <Mic className="w-7 h-7 text-white" />
                                    )}
                                </button>
                            );
                        })()}
                    </div>

                    {/* Dot wave animation */}
                    <div className="dot-wave mb-3 mt-2">
                        {[1,2,3,4,5].map(i => <div key={i} className="dot-bar" />)}
                    </div>

                    <p className="text-sm font-semibold text-white mb-0.5">
                        {isLoading
                            ? t('thinkingDots')
                            : isTtsLoading
                                ? t('preparingAudio')
                                : isLocalSTTActive
                                    ? t('listeningDots')
                                    : isTtsPlaying
                                        ? t('representativeSpeaking')
                                        : t('speakWhenReady')}
                    </p>
                    <p className="text-xs text-slate-400 mb-2">
                        {t('pressSpace', { key: '' })}<span className="px-1.5 py-0.5 bg-bg-700/80 border border-neon-cyan/30 rounded text-neon-cyan text-[10px] font-mono">{t('spaceKey')}</span>
                    </p>

                    {/* Detener audio */}
                    {isTtsPlaying && (
                        <button
                            data-tour="stop-audio"
                            onClick={stopTTS}
                            className="text-[11px] text-slate-200 hover:text-white border border-neon-magenta/40 rounded-full px-4 py-1.5 hover:bg-neon-magenta/10 transition-all mb-3"
                        >
                            {t('stopAudio')}
                        </button>
                    )}
                </div>
                </div>{/* /scrollable hero zone */}

                {/* ─── Chat flotante (bottom-left) ─── */}
                <div className="absolute bottom-4 left-4 z-20">
                    <button
                        data-tour="chat-toggle"
                        onClick={() => setIsChatOpen(!isChatOpen)}
                        className="relative w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:scale-105"
                        style={{ background: 'rgba(18, 18, 31, 0.85)', border: '1px solid rgba(34, 211, 238, 0.4)', boxShadow: '0 0 12px rgba(34, 211, 238, 0.2)' }}
                    >
                        <MessageCircle className="w-5 h-5 text-neon-cyan" />
                        {messages.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1" style={{ background: '#22d3ee' }}>
                                {messages.filter(m => m.role !== 'scene').length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Chat panel expandido — tamaño responsivo: angosto en
                    pantallas chicas (no taparía al personaje), crece en
                    breakpoints mayores. Alto capado para no comerse la
                    pantalla. */}
                {isChatOpen && (
                    <div className="absolute bottom-20 left-4 w-[min(calc(100vw-32px),320px)] md:w-[360px] lg:w-[400px] rounded-2xl z-20 animate-fade-in overflow-hidden flex flex-col" style={{ maxHeight: 'min(70vh, 560px)', background: 'rgba(10, 10, 20, 0.95)', border: '1px solid rgba(34, 211, 238, 0.3)', boxShadow: '0 0 32px rgba(34, 211, 238, 0.15), 0 16px 48px rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(12px)' }}>
                        <div className="p-3 border-b border-neon-cyan/10 flex items-center justify-between shrink-0">
                            <div className="min-w-0">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{t('conversation')}</span>
                                <p className="text-[11px] text-slate-300 truncate">{messages.filter(m => m.role !== 'scene').length} turnos · Desafío {(challengePoolDetail?.filter(c => c.status === 'completed').length || 0) + (challengePoolDetail?.find(c => c.status === 'active') ? 1 : 0)} de {challengePoolDetail?.length || 2}</p>
                            </div>
                            <button onClick={() => setIsChatOpen(false)} className="p-1 text-slate-400 hover:text-slate-200 transition-colors shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                            {messages.map((message, index) => (
                                <ChatMessage key={index} message={message} />
                            ))}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="flex items-center gap-2 px-4 py-2.5 bg-bg-700/50 rounded-xl">
                                        <div className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" />
                                        <div className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                        <div className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input de texto dentro del chat. El modo principal es
                            por voz; este campo queda accesible desde aquí para
                            quien prefiera escribir o necesite editar. */}
                        {isInitialized && !isInFeedbackPhase() && (
                            <div className="p-3 border-t border-neon-cyan/10 shrink-0">
                                <form data-tour="text-input" onSubmit={handleSubmit} className="flex gap-2 items-end">
                                    <div className="flex-1 relative">
                                        <textarea
                                            ref={inputRef}
                                            rows={1}
                                            value={inputMessage}
                                            onChange={(e) => { setInputMessage(e.target.value); setIsVoiceTranscribing(false); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (!isLoading && inputMessage.trim()) sendMessage(inputMessage);
                                                }
                                            }}
                                            placeholder={isLocalSTTActive ? t('transcribing') : t('writeTo')}
                                            disabled={isLoading}
                                            className={`w-full py-2.5 px-3 pr-8 rounded-xl text-sm focus:outline-none focus:ring-2 disabled:opacity-50 transition-all resize-none leading-relaxed ${
                                                isLocalSTTActive || isVoiceTranscribing
                                                    ? 'bg-neon-lime/10 border border-neon-lime/50 text-white placeholder-neon-lime/60 focus:ring-neon-lime'
                                                    : 'bg-bg-800/80 border border-neon-cyan/30 text-white placeholder-slate-500 focus:ring-neon-cyan/50'
                                            }`}
                                        />
                                        {(isLocalSTTActive || isVoiceTranscribing) && (
                                            <div className="absolute right-3 top-2.5 flex items-center gap-1">
                                                <div className="w-1.5 h-1.5 bg-neon-lime/100 rounded-full animate-pulse" />
                                                <div className="w-1.5 h-1.5 bg-neon-lime/100 rounded-full animate-pulse" style={{animationDelay: '0.2s'}} />
                                                <div className="w-1.5 h-1.5 bg-neon-lime/100 rounded-full animate-pulse" style={{animationDelay: '0.4s'}} />
                                            </div>
                                        )}
                                        {inputMessage && !isLocalSTTActive && !isVoiceTranscribing && (
                                            <button type="button" onClick={() => { setInputMessage(''); setIsVoiceTranscribing(false); }} className="absolute right-2 top-2 p-0.5 text-slate-400 hover:text-slate-200 transition-colors">
                                                <XCircle className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isLoading || !inputMessage.trim()}
                                        className="px-3 py-2.5 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:scale-105 shrink-0 font-bold"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.25), rgba(232, 121, 249, 0.25))',
                                            border: '1px solid rgba(232, 121, 249, 0.6)',
                                            color: '#fff',
                                            boxShadow: '0 0 12px rgba(232, 121, 249, 0.35), 0 0 24px rgba(34, 211, 238, 0.15)',
                                        }}
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Leyenda de colores (flotante a la derecha) ─── */}
                <div className="absolute top-[100px] right-4 z-30 flex flex-col items-end">
                    <button
                        onClick={() => setLegendOpen(o => !o)}
                        className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-300 hover:text-neon-cyan bg-bg-800/80 backdrop-blur-sm border border-neon-cyan/25 px-2.5 py-1.5 rounded-lg transition-colors"
                        aria-expanded={legendOpen}
                        title={t('legendTitle')}
                    >
                        <Info className="w-3 h-3" />
                        {t('legendTitle')}
                        <ChevronDown className={`w-3 h-3 transition-transform ${legendOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {legendOpen && (
                        <div className="mt-2 w-56 rounded-lg border border-neon-cyan/25 bg-bg-800/90 backdrop-blur-sm px-3 py-2.5 space-y-1.5">
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-neon-amber shadow-[0_0_6px_rgba(251,191,36,0.6)] shrink-0" />
                                <span className="text-[11px] text-slate-200">💡 {t('legendHint')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-neon-lime shadow-[0_0_6px_rgba(163,230,53,0.6)] shrink-0" />
                                <span className="text-[11px] text-slate-200">✓ {t('legendGoodMove')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 shadow-[0_0_6px_rgba(251,146,60,0.6)] shrink-0" />
                                <span className="text-[11px] text-slate-200">⚠ {t('legendWatchOut')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-neon-cyan shadow-[0_0_6px_rgba(34,211,238,0.6)] shrink-0" />
                                <span className="text-[11px] text-slate-200">→ {t('legendMoveAvailable')}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Toggle de subtítulos */}
                {isInitialized && !isInFeedbackPhase() && (
                    <button
                        onClick={toggleSubtitles}
                        className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-[10px] font-mono uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full transition-all ${
                            subtitlesEnabled
                                ? 'bg-bg-700/70 text-slate-200 border border-slate-500/40 hover:bg-bg-700/90'
                                : 'bg-bg-800/50 text-slate-500 border border-slate-700/40 hover:text-slate-300'
                        }`}
                        title={subtitlesEnabled ? t('subtitlesOn') : t('subtitlesOff')}
                    >
                        {subtitlesEnabled ? t('subtitlesOn') : t('subtitlesOff')}
                    </button>
                )}

                {/* Estados gating (initializing / generando feedback) — overlays
                    cortos en la parte inferior, ya que el campo de texto vive
                    ahora dentro del panel de chat. */}
                {(!isInitialized || isInFeedbackPhase()) && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                        {!isInitialized ? (
                            <div className="flex items-center justify-center p-3 rounded-xl" style={{ background: 'rgba(18, 18, 31, 0.7)', border: '1px solid rgba(34, 211, 238, 0.25)' }}>
                                <Loader2 className="w-4 h-4 text-neon-cyan animate-spin mr-2" />
                                <span className="text-xs font-mono tracking-widest uppercase text-neon-cyan/80">{t('initializing')}</span>
                            </div>
                        ) : (
                            <div className="p-3.5 bg-neon-amber/10 border border-neon-amber/30 rounded-xl text-center flex items-center justify-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 text-neon-amber animate-spin" />
                                <span className="text-xs text-neon-amber">{t('generatingFeedback')}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            </div>

            {/* Briefing inicial — overlay fullscreen con blur de fondo. Aparece
                una vez al entrar a la vista de simulación y se oculta al
                clickear INICIAR (setBriefingDismissed). Detrás se ve la
                simulación ya cargando para una transición fluida. */}
            {isFirstMessage && messages.length === 0 && !briefingDismissed && scenarioInfo && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div
                        className="w-full max-w-xl rounded-2xl border border-neon-cyan/30 p-6"
                        style={{
                            background: 'rgba(10, 10, 20, 0.96)',
                            boxShadow: '0 0 40px rgba(34, 211, 238, 0.2), 0 16px 60px rgba(0, 0, 0, 0.7)',
                        }}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin className="w-4 h-4 text-neon-cyan" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-cyan font-bold">{t('visitContext')}</span>
                        </div>
                        {scenarioInfo.tipo_proyecto && (
                            <p className="text-[11px] font-mono text-slate-400 mb-2 uppercase tracking-wider">{scenarioInfo.tipo_proyecto}</p>
                        )}
                        {(() => {
                            const raw = scenarioInfo.descripcion_detallada || scenarioInfo.descripcion_breve || '';
                            const items = raw
                                .split('•')
                                .map((s) => s.trim())
                                .filter(Boolean);
                            if (items.length > 1) {
                                return (
                                    <ul className="text-[13px] text-slate-200 leading-relaxed mb-4 space-y-1.5 list-none">
                                        {items.map((item, i) => (
                                            <li key={i} className="flex gap-2">
                                                <span className="text-neon-cyan shrink-0 leading-relaxed">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }
                            return (
                                <p className="text-[13px] text-slate-200 leading-relaxed mb-4">
                                    {raw}
                                </p>
                            );
                        })()}
                        <div
                            className="rounded-xl p-4 mb-5 border-l-4 border-neon-amber"
                            style={{ background: 'rgba(251, 191, 36, 0.08)' }}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-bg-900 text-xs font-bold bg-neon-amber">
                                    1
                                </div>
                                <p className="text-[11px] font-mono uppercase tracking-[0.18em] font-bold text-neon-amber">
                                    {t('howToStart')}
                                </p>
                            </div>
                            <p className="text-[13px] text-slate-200 leading-relaxed font-medium">
                                {t('howToStartDesc')}
                            </p>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            {/* Volver a la selección de escenarios — útil si el
                                usuario se equivocó al elegir. */}
                            <button
                                onClick={handleBackToScenarios}
                                className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-white px-3 py-2 rounded-lg hover:bg-bg-700/50 transition-colors"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {t('back')}
                            </button>
                            <button
                                onClick={() => {
                                    setBriefingDismissed(true);
                                    const greeting = selectedLanguage === 'en' ? 'Hi' : 'Hola';
                                    sendMessage(greeting, { hidden: true });
                                }}
                                disabled={!isAvatarReady}
                                className={`flex items-center gap-1.5 text-sm font-mono font-bold uppercase tracking-wider text-bg-900 px-5 py-2 rounded-lg transition-all shadow-[0_0_16px_rgba(163,230,53,0.4)] ${
                                    isAvatarReady ? 'hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'
                                }`}
                                style={{ background: '#a3e635' }}
                            >
                                {isAvatarReady ? (
                                    <>
                                        {t('start')}
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                ) : (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('loadingAvatar')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de ayuda */}
            {showHelpModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50">
                    <div className="rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" style={{ background: 'rgba(10, 10, 20, 0.95)', border: '1px solid rgba(34, 211, 238, 0.3)', boxShadow: '0 0 40px rgba(34, 211, 238, 0.2), 0 16px 60px rgba(0, 0, 0, 0.7)' }}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-white">{t('howItWorks')}</h3>
                            <button onClick={() => setShowHelpModal(false)} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-bg-700/50 rounded-lg transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Bloque del escenario activo: aparece arriba del help
                            genérico para que el usuario pueda releer el contexto
                            sin salir de la simulación. */}
                        {scenarioInfo?.nombre && (
                            <div className="mb-5 rounded-xl p-4 border border-neon-cyan/25" style={{ background: 'rgba(34, 211, 238, 0.06)' }}>
                                <div className="flex items-center gap-2 mb-2">
                                    <MapPin className="w-4 h-4 text-neon-cyan" />
                                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-cyan font-bold">
                                        {t('yourScenario')}
                                    </span>
                                </div>
                                <h4 className="text-sm font-semibold text-white mb-1">
                                    {scenarioInfo.nombre}
                                </h4>
                                {scenarioInfo.tipo_proyecto && (
                                    <p className="text-[11px] font-mono text-slate-400 uppercase tracking-wider mb-2">
                                        {scenarioInfo.tipo_proyecto}
                                    </p>
                                )}
                                {(scenarioInfo.nombre_personaje || scenarioInfo.cargo_personaje) && (
                                    <p className="text-xs text-slate-300 mb-2">
                                        <span className="text-slate-400">{t('yourInterlocutor')}: </span>
                                        <span className="text-white font-medium">{scenarioInfo.nombre_personaje}</span>
                                        {scenarioInfo.cargo_personaje && (
                                            <span className="text-slate-400"> — {scenarioInfo.cargo_personaje}</span>
                                        )}
                                    </p>
                                )}
                                <p className="text-[13px] text-slate-200 leading-relaxed">
                                    {scenarioInfo.descripcion_detallada || scenarioInfo.descripcion_breve}
                                </p>
                            </div>
                        )}

                        <div className="text-sm text-slate-200 mb-3">
                            {t('helpIntro', {
                                voice: `<b>${t('voice')}</b>`,
                                text: `<b>${t('text')}</b>`,
                            }).split(/<b>|<\/b>/).map((part, i) =>
                                i % 2 === 1
                                    ? <span key={i} className="text-white font-medium">{part}</span>
                                    : part
                            )}
                        </div>
                        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl mb-4" style={{ background: 'rgba(34, 211, 238, 0.1)' }}>
                            <span className="text-lg leading-none mt-0.5">💡</span>
                            <p className="text-sm font-medium" style={{ color: '#22d3ee' }}>{t('helpSoftSkillsNote')}</p>
                        </div>

                        <div className="space-y-3 text-sm">
                            <div className="border-l-4 border-neon-cyan pl-4">
                                <p className="font-medium text-neon-cyan">{t('phase1')}</p>
                                <p className="text-slate-300">{t('phase1Desc')}</p>
                            </div>
                            <div className="border-l-4 border-neon-amber pl-4">
                                <p className="font-medium text-neon-amber">{t('phase2')}</p>
                                <p className="text-slate-300">{t('phase2Desc')}</p>
                            </div>
                            <div className="border-l-4 pl-4" style={{ borderColor: '#22d3ee' }}>
                                <p className="font-medium" style={{ color: '#22d3ee' }}>{t('phase3')}</p>
                                <p className="text-slate-300">{t('phase3Desc')}</p>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center justify-end gap-3">
                            <button onClick={() => setShowHelpModal(false)} className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors text-sm" style={{ background: '#22d3ee' }}>
                                {t('understood')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </BackgroundStack>
    );
}

export default function App() {
    const [lang, setLang] = useState('es');
    return (
        <I18nProvider lang={lang}>
            <AppInner onLangChange={setLang} />
        </I18nProvider>
    );
}
